// Lightweight i18n — EN + FR dictionaries. Locale persisted in localStorage.
// Auto-select FR when country is francophone.
import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "fev.locale";
const FRANCOPHONE_COUNTRIES = new Set([
  "Cote d'Ivoire", "Senegal", "Guinea", "Mali", "Burkina Faso", "Niger",
  "Cameroon", "DRC", "Benin", "Togo", "Chad", "Gabon", "Congo",
]);

const DICT = {
  en: {
    // Nav
    "nav.configurator": "Configurator",
    "nav.methodology": "How it works",
    "nav.sectors": "Sectors",
    "nav.pricing": "Pricing",
    "nav.projects": "My projects",
    "nav.signin": "Sign in",
    "nav.signout": "Sign out",
    "nav.start_cta": "Start free first pass",
    "nav.start_cta_short": "Free first pass",
    "nav.credits": "report credits",
    "nav.back_to_site": "Back to site",
    // Footer
    "footer.disclaimer": "The Power Configurator provides indicative, screening-level outputs for information only and does not constitute engineering, financial or investment advice.",
    "footer.product": "Product",
    "footer.company": "Company",
    "footer.tagline": "Screening-level power scoping for industrial, mining, utilities and telecom buyers across Africa.",
    // Landing
    "landing.hero.tag": "Screening-level · indicative outputs",
    "landing.hero.title_1": "Scope your power project",
    "landing.hero.title_2": "in",
    "landing.hero.title_3": "a single pass.",
    "landing.hero.subtitle": "A professional tool for industrial, mining, utilities and telecom buyers. Enter your project parameters — sector, location, load, site conditions, grid, ownership and priorities — and receive a first-pass power scope with an optimised generation mix, budgetary costs, permitting outlook and an initial project plan.",
    "landing.hero.start": "Start your free first pass",
    "landing.hero.talk": "Contact our team",
    "landing.hero.see_pricing": "See pricing",
    "landing.method.title_1": "A defensible, transparent configurator",
    "landing.method.overline": "Methodology",
    "landing.pricing.title": "Free first pass. Detailed reports and bundles.",
    "landing.pricing.overline": "Report pricing",
    "landing.pricing.single_desc": "Full breakdown, memorandum PDF, financial plan for one project.",
    "landing.pricing.bundle_desc": "reports · never expire",
    "landing.sectors.title_1": "Built for professional buyers",
    "landing.sectors.overline": "Buyers we serve",
    "landing.sectors.desc": "The Power Configurator focuses on projects with a clear commercial off-taker or captive user — not residential kits.",
    "landing.sectors.industrial": "Manufacturing, warehousing, agro-processing",
    "landing.sectors.mining": "On-site power for open-pit and underground operations",
    "landing.sectors.utilities": "Independent power producers, distribution & mini-grids",
    "landing.sectors.telecom": "Base stations, tower networks, data centres",
    "landing.hero.countries": "✓ 20+ countries",
    "landing.hero.west_africa": "✓ West Africa (Nigeria, Ghana, Côte d'Ivoire, Senegal): detailed permitting",
    "landing.hero.site_prefilled": "✓ Site conditions pre-filled per country, editable",
    "landing.sample.overline": "Sample first-pass output",
    "landing.sample.mix": "Mix",
    "landing.sample.mix_v": "Solar + BESS + Diesel N+1",
    "landing.sample.firm": "Firm capacity",
    "landing.sample.firm_v": "2.4 MW",
    "landing.sample.renewable": "Renewable frac.",
    "landing.sample.renewable_v": "62%",
    "landing.sample.capex": "CAPEX range",
    "landing.sample.capex_v": "$4.1 – 5.8m",
    "landing.sample.lcoe_v": "$0.156 / kWh",
    "landing.sample.note": "Illustrative preview. Free first pass shows the mix and cost range; full breakdown, memorandum PDF and financial plan are in the paid report.",
    "landing.method.body": "Site conditions are pre-filled for each country and you can edit them. The report shows the main costs and assumptions behind the recommendation.",
    "landing.method.s1.t": "Load & site",
    "landing.method.s1.d": "Peak/energy, load pattern, altitude, ambient, irradiance — pre-filled per country, editable.",
    "landing.method.s2.t": "Grid & ownership",
    "landing.method.s2.d": "On-grid, weak-grid or off-grid × captive, IPP, BOO, PPP, ESCO, lease.",
    "landing.method.s3.t": "Optimise mix",
    "landing.method.s3.d": "Several plant options are sized for your site, and the one that best fits your priority is recommended. Only dispatchable capacity counts toward firm.",
    "landing.method.s4.t": "Cost, permits, plan",
    "landing.method.s4.d": "Indicative CAPEX, OPEX, LCOE (±range), permitting by country, milestone schedule.",
    "landing.pricing.desc": "Bundles are prepaid credits, redeemable within 12 months, decremented one report at a time. Every report contains the same full scope.",
    "landing.pricing.first.overline": "First pass",
    "landing.pricing.first.price": "Free",
    "landing.pricing.first.desc": "Automated rough sizing and indicative cost range. Any project. No purchase required.",
    "landing.pricing.first.b1": "✓ Sector + country + load in 7 steps",
    "landing.pricing.first.b2": "✓ Generation mix (solar / battery / diesel / gas)",
    "landing.pricing.first.b3": "✓ Firm capacity check + renewable share",
    "landing.pricing.first.b4": "✓ CAPEX range (low · expected · high)",
    "landing.pricing.single.overline": "Detailed report",
    "landing.pricing.single.badge": "Most popular",
    "landing.pricing.single.desc": "One-time · one full report.",
    "landing.pricing.single.b1": "✓ Full power model + equipment list",
    "landing.pricing.single.b2": "✓ CAPEX / OPEX breakdown + LCOE",
    "landing.pricing.single.b3": "✓ Country-specific permitting map",
    "landing.pricing.single.b4": "✓ Feasibility PDF + CSV export",
    "landing.pricing.single.b5": "✓ Team share link",
    "landing.pricing.single.cta": "Get a detailed report",
    "landing.pricing.bundle.overline": "Bundle · {n} reports",
    "landing.pricing.bundle.badge": "Recommended",
    "landing.pricing.bundle.per_report": "/report",
    "landing.pricing.bundle.save": " · save {amount}",
    "landing.pricing.bundle.b1": "✓ {n} prepaid report credits",
    "landing.pricing.bundle.b2": "✓ Redeem within 12 months",
    "landing.pricing.bundle.b3": "✓ One-click unlock per project",
    "landing.pricing.bundle.b4": "✓ Consolidated invoice",
    "landing.pricing.bundle.cta": "Buy bundle",
    "landing.pricing.ent.overline": "Custom / Enterprise",
    "landing.pricing.ent.desc": "Set by report volume + seats · declining per-report software rate.",
    "landing.pricing.ent.b1": "✓ Annual software plan or prepaid credit balance",
    "landing.pricing.ent.b2": "✓ Volume-tiered per-report rate",
    "landing.pricing.ent.b3": "✓ For in-house teams running many project scopes",
    "landing.pricing.ent.b4": "✓ Seats for your team",
    "landing.pricing.ent.cta": "Talk to an account manager",
    "landing.pricing.ext.overline": "Extended workflow",
    "landing.pricing.ext.price": "By arrangement",
    "landing.pricing.ext.desc": "Continue the workflow beyond the automated report.",
    "landing.pricing.ext.b1": "✓ Extend a saved scope beyond the automated output",
    "landing.pricing.ext.b2": "✓ Discussed separately from software pricing",
    "landing.pricing.ext.b3": "✓ Report credits count towards the engagement",
    "landing.cta.title": "Run more scopes, faster.",
    "landing.cta.body": "A first pass is free — pay only when you unlock the full software report. Buy in bundles to bring the per-report rate down.",
    // Configurator
    "wiz.title": "Configure your project",
    "wiz.overline": "Free first pass",
    "wiz.step": "Step",
    "wiz.of": "of",
    "wiz.next": "Next",
    "wiz.back": "Back",
    "wiz.generate": "Generate first pass",
    "wiz.running": "Running…",
    "wiz.step.1": "Sector & application",
    "wiz.step.2": "Location",
    "wiz.step.3": "Load & demand",
    "wiz.step.4": "Site & ambient",
    "wiz.step.5": "Grid situation",
    "wiz.step.6": "Ownership model",
    "wiz.step.7": "Priority",
    "wiz.priority.title": "Priority",
    "wiz.lf.title": "Load factor presets",
    "wiz.load.derived": "Derived from peak and daily energy (daily energy ÷ (peak × 24)).",
    "wiz.load.err_above": "Daily energy is more than peak × 24 h. Check the peak and the daily energy, and their units (kW vs MW, kWh vs MWh).",
    "wiz.load.err_below": "Daily energy is less than one hour at peak. Check the peak and the daily energy, and their units (kW vs MW, kWh vs MWh).",
    "wiz.load.warn_peaky": "Unusually peaky load — check your inputs.",
    "wiz.lf.hint": "These set the load factor — the ratio of average to peak demand. The 24-hour shape is taken from your sector.",
    "wiz.lf.p80": "Load factor 0.80",
    "wiz.lf.p80.sub": "Average demand ≈ 80% of peak — round-the-clock operation",
    "wiz.lf.p45": "Load factor 0.45",
    "wiz.lf.p45.sub": "Average demand ≈ 45% of peak — day-time operation",
    "wiz.lf.p35": "Load factor 0.35",
    "wiz.lf.p35.sub": "Average demand ≈ 35% of peak — variable or seasonal",
    "wiz.priority.sub": "How should the configurator break ties?",
    "wiz.priority.lowest_upfront": "Lowest upfront cost",
    "wiz.priority.lowest_lifetime": "Lowest lifetime cost",
    "wiz.priority.max_reliability": "Maximum reliability",
    "wiz.priority.lowest_emission": "Lowest emissions",
    "wiz.priority.banner": "Once you generate the first pass, we'll show the recommended mix and cost range. The full breakdown, permitting checklist, memorandum PDF and financial plan are in the paid report.",
    "wiz.gas_avail.title": "Gas availability",
    "wiz.gas_avail.hint": "Pick your fuel-supply situation. If you're not sure, choose \"unknown\" — we'll still price gas candidates but flag the recommendation as contingent on a confirmed supply.",
    "wiz.gas_avail.pipeline": "Pipeline gas available",
    "wiz.gas_avail.vp": "Virtual pipeline (LNG / CNG)",
    "wiz.gas_avail.none": "No gas available",
    "wiz.gas_avail.unknown": "Unknown / to be confirmed",
    "wiz.diesel_avail.title": "Diesel availability",
    "wiz.diesel_avail.hint": "Can diesel be delivered and stored on site? \"Available\" assumes deliveries as needed, even where supply is irregular. Pick \"black-start only\" if diesel can be kept only to start a gas plant, or \"none\" if the site cannot use diesel at all. Both limit the recommendation to gas plants.",
    "wiz.diesel_avail.available": "Available (deliveries + on-site storage)",
    "wiz.diesel_avail.constrained": "Black-start only (no regular supply)",
    "wiz.diesel_avail.none": "None (site cannot use diesel)",
    "wiz.thermal.title": "Advanced: thermal & gas (optional)",
    "wiz.thermal.sub": "For utility, mining and industrial scoping · leave blank for solar-first sizing",
    "wiz.disclaimer": "The Power Configurator provides indicative, screening-level outputs for information only and does not constitute engineering, financial or investment advice.",
    // Results
    "results.tag": "Indicative · Screening-level",
    "results.title": "First-pass power scope",
    "results.reconfigure": "Reconfigure",
    "results.save": "Save project",
    "results.saved": "Saved",
    "results.saving": "Saving…",
    "results.talk": "Contact our team",
    "results.priority_label": "Priority:",
    "results.priority.lowest_upfront": "lowest upfront",
    "results.priority.lowest_lifetime": "lowest lifetime",
    "results.priority.max_reliability": "max reliability",
    "results.priority.lowest_emission": "lowest emissions",
    "results.block.1": "Optimised power model",
    "results.block.1_sub": "Recommended mix",
    "results.free_preview": "Free preview",
    "results.annual_energy": "Annual served:",
    "results.annual_demand": "Annual demand:",
    "results.annual_served": "Annual served:",
    "results.annual_generation": "Annual generation:",
    "results.renewable_fraction": "Renewable fraction:",
    "results.tile.solar": "Solar PV",
    "results.tile.battery": "Battery",
    "results.tile.diesel": "Diesel gensets",
    "results.tile.diesel_blackstart": "Diesel black-start (standby)",
    "results.tile.ocgt": "Gas turbines (OCGT)",
    "results.tile.ccgt": "Combined cycle (CCGT)",
    "results.tile.wind": "Wind",
    "results.tile.hfo": "HFO",
    "results.tile.gas": "Gas engines",
    "results.tile.firm": "Firm capacity",
    "results.tile.derate": "Generator derate",
    "results.block.1b": "Your recalculated model",
    "results.block.1b_title": "Your configuration",
    "results.adjusted_mix": "Adjusted mix",
    "results.adjusted.delta_basis": "Changes (Δ) are measured against the recommended plant:",
    "results.adjusted.delta_basis_generic": "Changes (Δ) are measured against the recommended configuration.",
    "results.thermal.overline": "Full-thermal baseload · alternative scenario",
    "results.thermal.title": "Full-thermal baseload — alternative scenario",
    "results.thermal.sub": "Standalone gas-only design sized to peak × 1.20. Compare it with the recommended plant above on upfront CAPEX and on lifetime cost.",
    "results.thermal.screening": "Screening",
    "results.thermal.capex": "Thermal CAPEX",
    "results.thermal.fuel_cost": "Annual fuel cost",
    "results.thermal.generation": "Annual generation",
    "results.thermal.cod": "Equipment lead + build",
    "results.thermal.gas_engine": "Gas engine",
    "results.thermal.derated": "derated",
    "results.thermal.mo": "mo",
    "results.thermal.gas_unconfirmed": "Gas supply not confirmed — costs assume {price} delivered",
    "thermal.gate.disabled": "Disabled by administrator.",
    "thermal.gate.below_min": "Site peak {peak} is below the {min} minimum for this technology.",
    "results.thermal.build": "build",
    "results.thermal.water": "water needed",
    "results.thermal.chp_recovered": "recovered heat",
    "results.thermal.lead": "Lead",
    "results.thermal.total_capex": "Total CAPEX (all-in)",
    "results.thermal.lcoe": "LCOE",
    "results.thermal.opex": "Annual OPEX",
    "results.thermal.firm_capacity": "Capacity after derating",
    "results.thermal.scenario_pill": "Alternative scenario",
    "results.thermal.full_thermal_recommended": "Full-thermal recommended",
    "results.thermal.suppressed_reason": "The recommended design carries no renewable capacity above the minimum share, so it is a full-thermal recommendation. No head-to-head is shown, because both scenarios would be the same plant.",
    "results.thermal.compare_title": "Head-to-head scenario comparison",
    "results.thermal.compare_two_plants": "These are two different plants: the configurator's recommendation and the thermal plant selected in step 7.",
    "results.thermal.compare_sub": "Both scenarios sized to the same peak load (× 1.20 firm capacity). The preferred scenario for procurement drives the paid PDF report.",
    "results.thermal.hybrid": "Recommended plant",
    "results.thermal.baseload": "Full-thermal baseload",
    "results.thermal.buyer_selection": "Your thermal selection",
    "results.thermal.metric_capex": "All-in CAPEX",
    "results.thermal.metric_lcoe": "LCOE",
    "results.thermal.metric_annual_opex": "Annual OPEX",
    "results.thermal.metric_renewable": "Renewable share",
    "results.thermal.preferred_flag": "Preferred for report",
    "results.thermal.set_preferred": "Set as preferred",
    "results.thermal.preferred_note": "This scenario will be highlighted on your paid PDF report as the buyer-preferred procurement path.",
    "results.thermal.npv_title": "20-yr lifetime cost (TCO)",
    "results.thermal.npv_sub": "Present value at {rate} % nominal (≈ {real} % real) of CAPEX, fuel / O&M / grid escalated at {esc} %/yr, any battery replacement and overhauls, over 20 yr — the configurator's figure.",
    "results.thermal.npv_years": "20 yr",
    "results.thermal.emissions_title": "Operational CO₂/yr",
    "results.thermal.emissions_sub": "Direct emissions from dispatched generation. The recommended mix is priced on whatever it dispatches — gas engines, diesel or grid; thermal-only = gas turbines/engines net of CHP credit.",
    "results.thermal.tonnes_yr": "t CO₂/yr",
    "results.thermal.csv_download": "Download comparison CSV",
    "results.thermal.csv_pending": "Preparing…",
    "results.thermal.gas_slider_label": "What if gas price =",
    "results.thermal.gas_slider_preview": "Preview",
    "results.thermal.gas_slider_authoritative": "Live",
    "results.thermal.gas_slider_recalculating": "Recalculating…",
    "results.thermal.gas_reset": "Reset",
    "results.thermal.preset_gulf": "Gulf",
    "results.thermal.preset_wafrica": "W. Africa avg",
    "results.thermal.preset_lng": "LNG spot",
    "results.thermal.notes_label": "Buyer rationale for lender / procurement",
    "results.thermal.notes_placeholder": "e.g. Prefer full-thermal — cheap pipeline gas, no rooftop for solar, need firm dispatch for smelter arc furnace.",
    "results.thermal.notes_char_count": "chars",
    "results.thermal.notes_max": "500 max",
    "results.thermal.save_compare_pdf": "Save comparison as PDF",
    "results.thermal.save_compare_pdf_pending": "Building PDF…",
    "results.tte.title": "Talk to an engineer",
    "results.tte.body": "This is a custom project. Want to go deeper with a tailored solution? Our engineering team can size a bespoke architecture beyond the standard candidates, run a financeable feasibility, and advise on procurement + EPC.",
    "results.tte.cta": "Email an engineer",
    "results.tte.email": "engineering@futureenergyventure.com",
    "results.back_to_priority": "Back",
    "results.back_to_priority_tip": "Back to Priority step — keep inputs",
    "results.reconfirm_title": "Rebuild project from the beginning?",
    "results.reconfirm_body": "All current configuration inputs (site, load, priority, thermal choices) will be reset. To make minor changes without starting over, use the Back arrow.",
    "results.reconfirm_confirm": "Rebuild from scratch",
    "results.reconfirm_cancel": "Cancel",
    "results.firm_shortfall_overline": "Firm capacity shortfall",
    "results.firm_shortfall_title": "The recommended mix does not meet the 20% reserve margin",
    "results.firm_shortfall_body": "Firm capacity available ({available} kW) is below the target of {target} kW (peak {peak} kW × 1.20). Add units, upgrade unit size, or accept a lower reserve margin before advancing to feasibility.",
    "results.unmet_load_overline": "Unmet energy over time",
    "results.unmet_load_title": "Dispatch leaves energy shortfall during low-solar / drained-battery periods",
    "results.unmet_load_body": "Screening dispatch shows about {unmet_mwh} MWh/yr ({pct}% of annual demand) that the current sizing cannot cover — hours when the plant's available sources cannot pick up the full load. Distinct from firm-capacity (kW at peak): this is energy over time (kWh). Add firm or stored capacity, or plan controlled load-shedding.",
    "results.cond_fuel_overline": "Fuel supply — confirmation needed",
    "results.cond_fuel_title": "Conditional on confirmed gas supply — recommended mix assumes pipeline or virtual pipeline gas",
    "results.cond_fuel_body": "You selected \"unknown\" for gas availability. This screening keeps gas-fired candidates in the comparison, but the recommended architecture — and its CAPEX, LCOE, and CO₂ figures — are contingent on a firm fuel supply. Confirm pipeline capacity or a virtual-pipeline (LNG / CNG) contract before advancing to feasibility.",
    "results.firm_buildup_title": "Firm capacity build-up (what dispatch actually saw)",
    "results.firm_dispatchable_kw": "Dispatchable capacity (gensets + gas engines + turbines)",
    "results.firm_battery_derated_kw": "Battery counted toward firm",
    "results.firm_battery_rated_kw": "Battery rated power",
    "results.firm_available_kw": "Firm available",
    "results.firm_target_kw": "Firm target (peak × 1.20)",
    "results.firm_low_sun_kw": "Stress case (battery at the charge it holds before the 4-h peak window)",
    "results.firm_low_sun_kw_nobatt": "Stress case (dispatchable capacity only)",
    "results.firm_status_green": "FIRM CAPACITY OK",
    "results.firm_status_amber": "FIRM NOT MET — UNSERVED ENERGY > 0",
    "results.firm_status_red": "FIRM SHORTFALL AT LOW-SUN STRESS",
    "results.firm_status_n1_fail": "FIRM OK — N-1 SHORTFALL: LARGEST UNIT OUT LEAVES THE SITE SHORT",
    "results.config_saved": "Your configuration was saved to this project.",
    "results.stale_overrides_overline": "Saved edits — partial restore",
    "results.stale_overrides_title": "{n} of your saved edits no longer apply",
    "results.stale_overrides_footer": "The current configurator could not honor these edits (input clamped, technology retired, or benchmark changed). Everything else was restored. Recompute to lock in the new values.",
    "results.stale_overrides_dismiss": "Dismiss",
    "results.thermal_adj.overline": "Your thermal configuration",
    "results.thermal_adj.title": "Adjusted gas & thermal",
    "results.thermal_adj.sub": "Δ badges compare each metric vs the recommended thermal mix above.",
    "results.thermal_adj.pill": "Adjusted",
    "results.thermal.cf": "Capacity factor",
    "results.thermal.total_capex_allin": "Total CAPEX (all-in)",
    "results.thermal.cf_line": "Capacity factor {cf}% — runs at {cf}% of installed capacity on average",
    "results.thermal.serves_line": "Serves {pct}% of site load",
    "results.thermal.partload_line": "Average efficiency η {eta}% — {share}% of running hours below 60% unit load",
    "results.thermal.partload_line_eta": "Average efficiency η {eta}%",
    "results.thermal.partload_line_diesel": "Average fuel use {lpk} l/kWh — {share}% of running hours below 60% unit load",
    "results.thermal.partload_line_diesel_lpk": "Average fuel use {lpk} l/kWh",
    "results.thermal_editor.oversize": "This bank is {mult}× the firm target of {target} kW — check the unit count and size.",
    "results.thermal.use_plant": "Use this plant in my configuration",
    "results.thermal.use_plant_note": "Replaces your configuration with this plant; Reset returns to the recommendation.",

    "results.thermal_adj.scope_in_mix": "Part of “Your configuration” — these units are in the plant you recalculated.",
    "results.thermal_adj.scope_separate": "Separate scenario — these thermal units are not part of “Your configuration” above.",
    "results.thermal_adj.unmet": "Unmet demand:",
    "results.range.title": "Indicative CAPEX range",
    "results.range.central": "Central estimate",
    "results.range.region_mult": "Region multiplier",
    "results.range.low": "low",
    "results.range.central_short": "central",
    "results.range.high": "high",
    "results.carbon.overline": "Estimated carbon footprint",
    "results.carbon.unit": "t CO₂/yr",
    "results.carbon.intensity": "Intensity",
    "results.carbon.intensity_unit": "gCO₂/kWh",
    "results.carbon.recommended_mix": "Recommended mix",
    "results.carbon.scale_low": "0 — low",
    "results.carbon.scale_med": "200 — medium",
    "results.carbon.scale_high": "500 — high",
    "results.carbon.your_config": "Your configuration",
    "results.carbon.rating.low": "LOW",
    "results.carbon.rating.medium": "MEDIUM",
    "results.carbon.rating.high": "HIGH",
    "results.carbon.drop_into_low": "Drop into Low",
    "results.carbon.tweak.chp_copy": "Enable CHP (combined heat + power) on your {{tech}} — the boiler-avoidance credit typically drops net emissions ~15–25 %, to about {{projected}} gCO₂/kWh, when the site has a real heat demand.",
    "results.carbon.reduce_emissions": "Reduce emissions",
    "results.carbon.tech.gas_engine": "gas engines",
    "results.carbon.tech.ocgt": "gas turbines (OCGT)",
    "results.carbon.tech.ccgt": "combined-cycle plant (CCGT)",
    "results.carbon.tweak.chp_badge": "Try CHP",
    "results.carbon.tweak.ppa_copy": "Procure a renewable-electricity PPA (or bundle guarantees of origin) for the grid-import share — a 0-carbon grid factor drops the score immediately without changing on-site kit.",
    "results.carbon.tweak.ppa_badge": "Try renewable PPA",
    "results.carbon.tweak.solar_battery_copy": "Try adding ~{{battery}} kWh of battery capacity and ~{{solar}} kWp of solar. That would time-shift roughly {{diesel}} MWh/yr of diesel dispatch and drop the score into the Low band.",
    "results.carbon.tweak.solar_battery_badge": "Try +{{battery}} kWh battery",
    "results.carbon.tweak.try_it": "Try it — pre-fill the Adjust panel",
    "results.carbon.applied_toast": "Pre-filled the Adjust panel — recalculating…",
    "results.paywall.overline": "Detailed report",
    "results.paywall.title": "Unlock the full first-pass package",
    "results.paywall.no_viable": "No plant meets your site's needs with these inputs, so there is no report to buy. Change the inputs, for example fuel availability, and run it again.",
    // Results page FR (a07c) — EN unchanged
    "pdf.unavailable": "Your report could not be generated just now. Please try again in a few minutes.",
    "pdf.download_failed": "Could not download the report",
    "results.head.peak": "Peak",
    "results.head.firm_target": "Firm target",
    "frontier.title": "What you'd get under each priority — same site, four objectives",
    "frontier.yours": "Yours",
    "frontier.pinned_h": "Your pinned configuration",
    "frontier.pinned_body": "Not a contest entry — this plant was set by you, so it has no ranking against the alternatives.",
    "frontier.floor_title": "Sizing constrained by component floor, not optimised",
    "frontier.floor": "at floor",
    "frontier.tco": "20-yr TCO",
    "frontier.aria": "Alternative priority winners",
    "footer.terms": "Terms of Service",
    "footer.privacy": "Privacy Notice",
    "footer.refund": "Refund Policy",
    // How it works + Field help (Prompt L)
    "how.title": "How the Power Configurator works",
    "how.show": "Show",
    "how.hide": "Hide",
    "how.summary": "We size several power plant options for your site, keep only those that can reliably meet your demand, and recommend the one that best fits your priority. Results are compared with what you would do without the project: a diesel-only plant if you are off-grid, grid supply plus diesel back-up on a weak grid, or grid supply if your connection is reliable.",
    "how.b1.title": "1 · What it does",
    "how.b1.body": "From your load, location, grid situation and site conditions, the configurator builds several plant options — solar, batteries, diesel or gas generators and the grid — and sizes each one for your site. Every option must provide firm capacity of at least 120% of your peak, from sources available at any hour: generators, batteries and, where it is reliable, the grid.",
    "how.b2.title": "2 · How the recommendation is chosen",
    "how.b2.body": "Among the options that pass this reliability check, it recommends the one that best matches the priority you pick in the last step: lowest upfront cost, lowest lifetime cost, maximum reliability or lowest emissions. \"Best\" therefore means best for your priority — change the priority and the recommendation can change.",
    "how.b3.title": "3 · What it is compared against",
    "how.b3.intro": "Every saving and return is measured against what you would do without the project. This depends on your grid situation:",
    "how.b3.off_grid.t": "Off-grid",
    "how.b3.off_grid.d": "a diesel-only power plant sized for the same load (investment, fuel and maintenance).",
    "how.b3.weak_grid.t": "Weak grid",
    "how.b3.weak_grid.d": "grid supply plus diesel back-up to cover the outages.",
    "how.b3.on_grid.t": "On-grid (reliable)",
    "how.b3.on_grid.d": "buying the same energy from the grid at the national tariff. Grid connection costs are not included.",
    "how.b3.none.t": "No comparison",
    "how.b3.none.d": "if you adjust the mix yourself, or if an off-grid site can use diesel only for black-start or not at all. The plant is still sized and costed, but no savings, payback or returns are shown.",
    "how.b3.yours": "Your selection",
    "how.b4.title": "4 · How savings, NPV and IRR are calculated",
    "how.b4.body": "Annual savings = the yearly running cost of the comparison case (fuel, grid bills, maintenance) minus the yearly running cost of the recommended plant — not fuel savings alone. The free result shows an indicative payback; the full report adds NPV and IRR: the plant's full investment is repaid by these savings over 20 years, at a 12% discount rate (default), with savings rising 3.5% a year and, if the plant has batteries, a battery replacement in year 10.",
    "how.use.title": "How to use the tool",
    "how.use.body": "Complete the 7 steps — most fields are pre-filled for your country, so you can generate a first pass with the defaults and refine it later. Hover over or tap the ⓘ next to any field to see what it means and how it affects the result. The free first pass shows the recommended mix, its costs and an indicative payback; savings, NPV and IRR are in the full report.",
    "how.scope": "Screening-level, indicative figures to decide whether a project is worth developing further — not an engineering design or investment advice.",
    "how.results_link": "How these results are calculated",
    "help.aria": "More information:",
    "help.sector": "Sets the typical 24-hour load profile of your site and a starting load factor (Industrial 0.70, Mining 0.85, Utilities 0.60, Telecom 0.75). You can change the load factor in Step 3.",
    "help.application": "Describes your site in the results and the report. It does not change the sizing — the load profile comes from the sector and the figures you enter in Step 3.",
    "help.country": "Sets the regional cost level (cost multiplier), the default solar resource, fuel price, grid tariff and typical outage hours, and the permitting rules shown in the report. Pre-filled values can be changed in the next steps.",
    "help.project_name": "Optional. Used only to name the project in My projects and on the report.",
    "help.peak_kw": "The highest power your site draws at any moment, in kW. Every option must provide firm capacity of at least 120% of this value — the target is shown under the field.",
    "help.day_kwh": "Total energy your site uses in a typical day, in kWh — from bills or meter data if you have them. Leave it at 0 and we calculate it as peak × load factor × 24 h. If you enter both, keep them consistent with the load factor.",
    "help.lf_presets": "Shortcuts that set the load factor below. Pick the one closest to how your site runs.",
    "help.load_factor": "Average demand divided by peak demand. 0.80 means the site runs close to its peak around the clock; 0.35 means short peaks and a low average. It sets how much energy you use, and so how much solar, storage and fuel the plant needs.",
    "help.altitude": "Site height above sea level. Generators lose about 4% of their rating for every 300 m above 1,000 m, so we oversize them to compensate.",
    "help.ambient": "The highest temperature the plant must run in. Generators lose about 2% of their rating for every 5 °C above 40 °C. Very hot, sunny sites also get slightly less output from each solar panel.",
    "help.irradiance": "Average daily solar energy on a flat surface, pre-filled for your country. The higher it is, the more energy each kW of solar produces. Replace it with measured or satellite data (e.g. Global Solar Atlas) if you have it.",
    "help.grid": "Your grid connection today. It decides what the project is compared against and which sources count toward firm capacity.",
    "help.grid.on_grid.d": "the grid supplies the site. Compared with buying the same energy at the national tariff.",
    "help.grid.weak_grid.d": "connected, with regular outages. Compared with grid supply plus diesel back-up.",
    "help.grid.off_grid.d": "no grid. Compared with a diesel-only power plant.",
    "help.outage_hours": "Average hours per day without grid power, recorded with your project. The comparison uses our country estimate of outage hours. The report says so, and you can replace it with metered figures at the feasibility stage.",
    "help.fuel_price": "Diesel price delivered to your site, in USD per litre, pre-filled for your country. It sets the fuel cost of any generators in the plant and of the diesel comparison case, so it has a large effect on savings: a higher fuel price usually means a shorter payback.",
    "help.ownership": "Who will own and run the plant. It decides which permits and licences are listed for your country. In this version the financial model uses the same indicative financing structure for every ownership type.",
    "help.own.captive.d": "you own the plant and use the power on site.",
    "help.own.IPP.d": "an independent producer sells the power to the grid or a utility.",
    "help.own.BOO.d": "a developer builds, owns and runs the plant and sells you the power.",
    "help.own.PPP.d": "a partnership between a public body and a private company.",
    "help.own.ESCO.d": "an energy service company supplies the power or the savings under a long-term contract.",
    "help.own.lease.d": "you rent the equipment for a fixed fee.",
    "help.priority": "What matters most to you. Only options that pass the reliability check are considered; this choice decides which of them is recommended.",
    "help.prio.lowest_upfront": "the smallest investment today. Options whose 20-year cost is more than twice the cheapest are ruled out.",
    "help.prio.lowest_lifetime": "the lowest total cost over 20 years: investment plus fuel, maintenance and grid bills, discounted.",
    "help.prio.max_reliability": "the cheapest option over 20 years that covers 120% of your peak with firm capacity and leaves no demand unserved.",
    "help.prio.lowest_emissions": "the lowest CO₂ per kWh, provided its 20-year cost stays within 25% of the cheapest option.",
    "help.gas_avail": "Combined-cycle plants (CCGT) need pipeline gas. A virtual pipeline (trucked LNG or CNG) suits gas engines and open-cycle turbines. \"No gas available\" rules gas plants out; \"Unknown\" keeps them in but marks the result as conditional on confirming supply.",
    "help.thermal.units": "Optional. Enter the number of units and the size of each (MW) to test your own thermal plant. It is costed as a thermal scenario shown alongside the recommendation. Leave the count at 0 to skip it.",
    "help.thermal.chp": "Combined heat and power: recovers about 40–50% of the fuel energy as useful heat (process heat, hot water or absorption cooling). Only worth it if the site has a steady heat demand.",
    "help.thermal.chp_host": "The thermal unit the heat-recovery equipment is fitted to.",
    "help.thermal.heat_demand": "Average heat the site can actually use, in kW thermal.",
    "help.thermal.gas_source": "How gas reaches the site. Pipeline gas is usually the cheapest; LNG, CNG and LPG are shipped or trucked in and cost more per GJ.",
    "help.thermal.gas_available": "Tick only if gas supply to this site is confirmed.",
    "help.thermal.gas_price": "Price of gas delivered to the site, in USD per gigajoule. To convert from USD/MMBtu, divide by 1.055.",
    "help.thermal.ambient": "Site temperature and altitude used to derate the thermal units. Gas turbines lose more output than gas engines in heat and at altitude.",
    "help.thermal.derate": "Reduces thermal unit ratings for site temperature and altitude. Leave it ticked unless you have site-specific manufacturer ratings.",
    "wiz.ownership.sub": "Sets the permits and licences listed for your country.",

    // Wizard labels (Prompt M)
    "wiz.sector.sub": "Which sector best describes the buyer?",
    "wiz.location.sub": "Country drives cost region, ambient defaults, grid assumptions and permitting rules.",
    "wiz.load.sub": "Provide peak demand kW and/or daily energy kWh/day. If you enter one, we derive the other from load factor.",
    "wiz.site.sub": "Pre-filled for {{country}}. Edit if you have measured data.",
    "wiz.grid.sub": "Drives which technologies count toward firm capacity.",
    "wiz.sector.industrial": "Industrial",
    "wiz.sector.mining": "Mining",
    "wiz.sector.utilities": "Utilities",
    "wiz.sector.telecom": "Telecom",
    "wiz.application.label": "Application",
    "wiz.application.placeholder": "Choose an application…",
    "wiz.app.manufacturing": "Manufacturing plant",
    "wiz.app.warehouse": "Warehouse / logistics",
    "wiz.app.agro": "Agro-processing",
    "wiz.app.cement": "Cement / heavy industry",
    "wiz.app.cold_chain": "Cold chain / food",
    "wiz.app.open_pit": "Open-pit mine",
    "wiz.app.underground": "Underground mine",
    "wiz.app.processing": "Processing plant",
    "wiz.app.camp": "Camp & offices",
    "wiz.app.exploration": "Exploration site",
    "wiz.app.ipp": "Independent power producer (IPP)",
    "wiz.app.distribution": "Distribution utility",
    "wiz.app.minigrid": "Mini-grid operator",
    "wiz.app.grid_renewable": "Grid-connected renewable",
    "wiz.app.macro_tower": "Macro tower site",
    "wiz.app.aggregation": "Regional aggregation site",
    "wiz.app.data_centre": "Data centre / edge",
    "wiz.app.fibre_hub": "Fibre network hub",
    "wiz.app.other": "Other",
    "wiz.app_other.label": "Describe the application",
    "wiz.app_other.placeholder": "e.g. Brick kiln",
    "wiz.country.label": "Country",
    "wiz.country.region": "Region:",
    "wiz.country.cost_mult": "Cost multiplier:",
    "wiz.project_name.label": "Project name (optional)",
    "wiz.project_name.placeholder": "e.g. Kaduna cement plant",
    "region.West Africa": "West Africa",
    "region.East Africa": "East Africa",
    "region.Central Africa": "Central Africa",
    "region.Southern Africa": "Southern Africa",
    "region.North Africa": "North Africa",
    "country.Algeria": "Algeria",
    "country.Angola": "Angola",
    "country.Benin": "Benin",
    "country.Botswana": "Botswana",
    "country.Burkina Faso": "Burkina Faso",
    "country.Cameroon": "Cameroon",
    "country.Chad": "Chad",
    "country.Cote d'Ivoire": "Côte d'Ivoire",
    "country.DRC": "DRC",
    "country.Egypt": "Egypt",
    "country.Ethiopia": "Ethiopia",
    "country.Gabon": "Gabon",
    "country.Ghana": "Ghana",
    "country.Guinea": "Guinea",
    "country.Guinea-Bissau": "Guinea-Bissau",
    "country.Kenya": "Kenya",
    "country.Liberia": "Liberia",
    "country.Mali": "Mali",
    "country.Mauritania": "Mauritania",
    "country.Morocco": "Morocco",
    "country.Mozambique": "Mozambique",
    "country.Namibia": "Namibia",
    "country.Niger": "Niger",
    "country.Nigeria": "Nigeria",
    "country.Rwanda": "Rwanda",
    "country.Senegal": "Senegal",
    "country.South Africa": "South Africa",
    "country.Tanzania": "Tanzania",
    "country.Togo": "Togo",
    "country.Uganda": "Uganda",
    "country.Zambia": "Zambia",
    "wiz.peak.label": "Peak demand (kW)",
    "wiz.peak_chip.peak": "Peak",
    "wiz.peak_chip.target": "→ firm capacity target (peak × 1.20)",
    "wiz.day_kwh.label": "Daily energy (kWh/day)",
    "wiz.lf.label": "Load factor (0.1–0.95)",
    "wiz.lf.derived_label": "Load factor",
    "wiz.altitude.label": "Altitude (m)",
    "wiz.ambient.label": "Design ambient temperature (°C)",
    "wiz.irradiance.label": "Solar irradiance (kWh/m²/day)",
    "wiz.site.derate_banner": "Genset ratings are derated ~4% per 300 m above 1000 m altitude and ~2% per 5 °C above 40 °C ambient. We compensate by oversizing.",
    "wiz.grid.on_grid": "On-grid (reliable)",
    "wiz.grid.weak_grid": "Weak grid (outages)",
    "wiz.grid.off_grid": "Off-grid",
    "wiz.outage.label": "Outage hours per day (average)",
    "wiz.fuel_price.label": "Fuel price (USD/L) — default for {{country}}",
    "wiz.own.captive": "Captive (own use)",
    "wiz.own.IPP": "IPP (sell to grid)",
    "wiz.own.BOO": "BOO (build-own-operate)",
    "wiz.own.PPP": "PPP",
    "wiz.own.ESCO": "ESCO (energy services)",
    "wiz.own.lease": "Lease / rental",
    "wiz.th.gas_engine.title": "Reciprocating gas engine",
    "wiz.th.gas_engine.body": "Modular 1–10 MW/unit, fast start, strong part-load, CHP-capable.",
    "wiz.th.ocgt.title": "Open-cycle gas turbine (OCGT)",
    "wiz.th.ocgt.body": "5–60 MW, peaking / fast-ramp, strong ambient derate.",
    "wiz.th.ccgt.title": "Combined-cycle (CCGT / CCP)",
    "wiz.th.ccgt.body": "30–500+ MW, highest efficiency, baseload, needs water.",
    "wiz.th.disabled": "Disabled by administrator.",
    "wiz.th.below_min": "Site peak {{peak}} MW is below the {{min}} MW minimum for this technology.",
    "wiz.th.count": "Count",
    "wiz.th.mw_unit": "MW/unit",
    "wiz.th.chp.title": "CHP / cogeneration",
    "wiz.th.chp.sub": "Recovers ~40–50% of fuel energy as useful heat (requires site thermal demand).",
    "wiz.th.chp.host": "Host prime mover",
    "wiz.th.heat_demand": "Site thermal demand (kW)",
    "wiz.th.gas_fuel": "Gas fuel",
    "wiz.th.source": "Source",
    "wiz.th.src.pipeline": "Pipeline gas",
    "wiz.th.src.lng": "LNG (imported / regas)",
    "wiz.th.src.cng": "CNG virtual pipeline (trucked-in)",
    "wiz.th.src.lpg": "LPG",
    "wiz.th.gas_available": "Gas available at site",
    "wiz.th.gas_price": "Delivered price ($/GJ)",
    "wiz.th.ambient.title": "Ambient (for thermal derating)",
    "wiz.th.temp": "Temperature (°C)",
    "wiz.th.altitude": "Altitude (m)",
    "wiz.th.derate": "Apply derate",
    "wiz.th.footnote": "All coefficients are indicative screening-level. Detailed sizing, OEM curves and gas contracts belong in the paid feasibility phase.",
    "wiz.toast.incomplete": "Please complete this step",
    "wiz.toast.compute_failed": "Could not compute. Please try again.",

    "results.paywall.desc": "Complete CAPEX/OPEX breakdown, LCOE, country-specific permitting, feasibility memorandum PDF, and initial project & financial plan.",
    "results.paywall.buy_report": "Buy report",
    "results.paywall.bundle": "Bundle",
    "results.paywall.use_credit": "Use 1 credit",
    "results.paywall.credits_left": "left",
    "results.paywall.promo_label": "Have a promo code?",
    "results.paywall.promo_placeholder": "Enter code",
    "results.paywall.promo_redeem": "Redeem",
    "results.paywall.promo_applying": "Applying…",
    "results.locked.equipment": "Equipment configuration",
    "results.locked.equipment_sub": "Component list",
    "results.locked.costs": "Budgetary costs",
    "results.locked.costs_sub": "CAPEX breakdown, OPEX, LCOE",
    "results.locked.permitting": "Permitting & regulation",
    "results.kpi.lcoe": "LCOE",
    "results.kpi.opex": "Annual OPEX",
    "results.kpi.payback": "Payback",
    "results.kpi.payback_hint": "Years to recover the plant's total CAPEX from its annual savings against the comparison case.",
    "results.kpi.life_used": "Analysis period",
    "results.report_unlocked": "Report unlocked",
    "results.open_report": "Open detailed report",
    "results.disclaimer": "The Power Configurator provides indicative, screening-level outputs for information only and does not constitute engineering, financial or investment advice.",
    "results.signin_save": "Sign in to save this project",
    "results.saved_adjusted": "Project saved with your adjusted configuration",
    "results.saved_ok": "Project saved",
    "results.save_failed": "Could not save",
    "results.save_failed_project": "Could not save project",
    "results.signin_buy": "Sign in to purchase the detailed report",
    "results.credit_unlocked": "Report unlocked using 1 credit",
    "results.no_credits": "No credits — please buy a bundle first",
    "results.unlock_failed": "Unlock failed",
    "results.enter_promo": "Enter a promo code",
    "results.promo_applied": "Promo code applied — report unlocked",
    "results.promo_failed": "Could not redeem code",
    "results.credit_added": "{{n}} credits added to your account",
    "results.credit_added_1": "1 credit added to your account",
    // Sensitivity
    "sens.title": "Flex fuel price & irradiance",
    "sens.title.fuel_only": "Flex fuel price",
    "sens.chart.no_solar": "This plant has no solar, so irradiance does not change its cost.",
    "frontier.unavailable": "Alternative unavailable for this input",
    "sens.overline": "Sensitivity",
    "sens.desc": "Drag the sliders; LCOE and payback update live.",
    "sens.fuel": "Diesel fuel price (USD/L)",
    "sens.axis.gas_price": "Delivered gas price (USD/GJ)",
    "sens.irradiance": "Solar irradiance (kWh/m²/day)",
    "sens.reset": "reset to baseline",
    "sens.baseline": "baseline",
    "sens.chart.title": "LCOE sensitivity curves",
    "sens.chart.desc": "Sweep from −30% to +30% of baseline. Marker shows the current slider value.",
    "sens.tile.lcoe": "LCOE",
    "sens.tile.capex": "CAPEX",
    "sens.tile.opex": "Annual OPEX",
    "sens.tile.payback": "Payback",
    "sens.tile.payback_yr": "yr",
    "sens.chart.xaxis": "% from baseline",
    "sens.chart.tooltip_axis": "from baseline",
    "sens.chart.legend.fuel": "LCOE vs fuel price",
    "sens.chart.legend.gas": "LCOE vs gas price",
    "sens.chart.legend.irr": "LCOE vs irradiance",
    "sens.chart.loading": "loading…",
    "sens.chart.baseline_prefix": "Baseline LCOE:",
    "sens.chart.baseline_suffix_fuel": "at fuel",
    "sens.chart.baseline_suffix_irr": "irr",
    // Compare
    "cmp.title": "Compare scenarios",
    "cmp.overline": "Comparison",
    "cmp.desc": "Select 2–4 saved projects to see them side-by-side. Best value per metric is highlighted in brand green, worst in the orange spark.",
    "cmp.back": "Back to projects",
    "cmp.saved": "Your saved projects",
    "cmp.pick_at_least_2": "Pick at least 2 projects to compare",
    "cmp.pick_at_least_2_sub": "Best per metric is highlighted in brand green; worst in orange.",
    "cmp.export_csv": "Export CSV",
    "report.export_csv": "Download CSV",
    "report.csv_downloaded": "CSV downloaded",
    // §12 roadmap — the phase months, the two milestones and the lead-time
    // line. The FACTS come from the engine's schedule; only the wording is
    // here, the same arrangement every other sentence on these pages uses.
    "report.roadmap.title": "Indicative milestone programme",
    "report.roadmap.phase.development": "Development & feasibility",
    "report.roadmap.phase.permitting": "Permitting",
    "report.roadmap.phase.engineering": "Engineering",
    "report.roadmap.phase.mobilization": "Mobilization",
    "report.roadmap.phase.construction": "Construction & commissioning",
    "report.roadmap.phase.equipment_lead": "Equipment lead time",
    "report.roadmap.phase.operation": "Operation",
    "report.roadmap.financial_close": "Financial close",
    "report.roadmap.cod": "COD",
    "report.roadmap.month": "month",
    "report.roadmap.months_label": "months",
    "report.roadmap.months": "months",
    "report.roadmap.from_month": "from month",
    "report.roadmap.ongoing": "ongoing",
    "report.roadmap.tech.gas_engine": "gas-engine",
    "report.roadmap.tech.ocgt": "OCGT",
    "report.roadmap.tech.ccgt": "CCGT",
    "report.roadmap.lead_note": "COD is set by the {tech} lead time ({lead} months + {build} months build), not by the base schedule.",
    // §12 COD on the free results page. One date, read from the engine's
    // schedule — the thermal cards' "Equipment lead + build" tile is a
    // DIFFERENT number (lead + build from order), which is why it no longer
    // calls itself a COD.
    "results.cod.title": "Commercial operation (COD)",
    "results.cod.month": "month",
    "results.cod.sub": "from project start",
    // Share modal
    "share.title": "Share this report",
    "share.overline": "Team sharing · read-only",
    "share.desc_prefix": "Anyone with the link can view",
    "share.desc_suffix": "as read-only. They cannot download raw data outside the PDF. You can revoke the link at any time.",
    "share.expiry": "Expires in",
    "share.expiry.7": "7 days",
    "share.expiry.30": "30 days",
    "share.expiry.90": "90 days",
    "share.expiry.never": "Never",
    "share.copy": "Copy",
    "share.copied": "Copied",
    "share.revoke": "Revoke link",
    "share.revoking": "Revoking…",
    "share.preview": "Open preview →",
    "share.generating": "Generating link…",
    // Common
    "common.language": "Language",
    "common.download_pdf": "Download PDF",
    "common.reset": "Reset",
  },
  fr: {
    // Nav
    "nav.configurator": "Configurateur",
    "nav.methodology": "Méthode",
    "nav.sectors": "Secteurs",
    "nav.pricing": "Tarifs",
    "nav.projects": "Mes projets",
    "nav.signin": "Se connecter",
    "nav.signout": "Déconnexion",
    "nav.start_cta": "Commencer l'analyse gratuite",
    "nav.start_cta_short": "Analyse gratuite",
    "nav.credits": "crédits report",
    "nav.back_to_site": "Retour au site",
    // Footer
    "footer.disclaimer": "Le Power Configurator fournit des résultats indicatifs, de niveau préliminaire, à titre informatif uniquement. Il ne constitue pas un conseil d'ingénierie, financier ou d'investissement.",
    "footer.product": "Produit",
    "footer.company": "Société",
    "footer.tagline": "Cadrage préliminaire de projets énergétiques pour les acheteurs industriels, miniers, télécoms et utilities à travers l'Afrique.",
    // Landing
    "landing.hero.tag": "Niveau préliminaire · résultats indicatifs",
    "landing.hero.title_1": "Cadrez votre projet énergétique",
    "landing.hero.title_2": "en",
    "landing.hero.title_3": "une seule passe.",
    "landing.hero.subtitle": "Un outil professionnel destiné aux acheteurs industriels, miniers, télécoms et utilities. Renseignez les paramètres du projet — secteur, localisation, consommation, site, réseau, mode de détention et priorités — et recevez un cadrage énergétique de première passe avec mix de production optimisé, coûts budgétaires, aperçu réglementaire et plan initial.",
    "landing.hero.start": "Commencer l'analyse gratuite",
    "landing.hero.talk": "Contacter notre équipe",
    "landing.hero.see_pricing": "Voir les tarifs",
    "landing.method.title_1": "Un configurateur rigoureux et transparent",
    "landing.method.overline": "Méthode",
    "landing.pricing.title": "Analyse gratuite. Rapports détaillés et packs.",
    "landing.pricing.overline": "Tarifs rapports",
    "landing.pricing.single_desc": "Détail complet, PDF du mémorandum, plan financier pour un projet.",
    "landing.pricing.bundle_desc": "rapports · sans expiration",
    "landing.sectors.title_1": "Conçu pour les acheteurs professionnels",
    "landing.sectors.overline": "Nos clients",
    "landing.sectors.desc": "Le configurateur s'adresse aux projets dotés d'un acheteur commercial identifié ou d'un utilisateur en autoproduction — pas aux kits résidentiels.",
    "landing.sectors.industrial": "Industrie manufacturière, entrepôts, agro-transformation",
    "landing.sectors.mining": "Énergie sur site pour les exploitations à ciel ouvert et souterraines",
    "landing.sectors.utilities": "Producteurs indépendants, distribution et mini-réseaux",
    "landing.sectors.telecom": "Stations de base, réseaux de pylônes, centres de données",
    "landing.hero.countries": "✓ Plus de 20\u00a0pays",
    "landing.hero.west_africa": "✓ Afrique de l'Ouest (Nigeria, Ghana, Côte d'Ivoire, Sénégal)\u00a0: réglementation détaillée",
    "landing.hero.site_prefilled": "✓ Conditions de site pré-remplies par pays, modifiables",
    "landing.sample.overline": "Exemple de résultat d'analyse gratuite",
    "landing.sample.mix": "Mix",
    "landing.sample.mix_v": "Solaire + BESS + diesel N+1",
    "landing.sample.firm": "Capacité ferme",
    "landing.sample.firm_v": "2,4\u00a0MW",
    "landing.sample.renewable": "Part renouvelable",
    "landing.sample.renewable_v": "62\u00a0%",
    "landing.sample.capex": "Fourchette de CAPEX",
    "landing.sample.capex_v": "4,1 – 5,8\u00a0M$",
    "landing.sample.lcoe_v": "0,156\u00a0$/kWh",
    "landing.sample.note": "Aperçu illustratif. L'analyse gratuite indique le mix et la fourchette de coûts\u00a0; le détail complet, le mémorandum PDF et le plan financier figurent dans le rapport payant.",
    "landing.method.body": "Les conditions de site sont pré-remplies pour chaque pays et vous pouvez les modifier. Le rapport présente les principaux coûts et hypothèses qui sous-tendent la recommandation.",
    "landing.method.s1.t": "Charge et site",
    "landing.method.s1.d": "Pointe/énergie, profil de charge, altitude, température ambiante, irradiation — pré-remplis par pays, modifiables.",
    "landing.method.s2.t": "Réseau et détention",
    "landing.method.s2.d": "Raccordé au réseau, réseau faible ou hors réseau × autoproduction, IPP, BOO, PPP, ESCO, location.",
    "landing.method.s3.t": "Optimisation du mix",
    "landing.method.s3.d": "Plusieurs options de centrale sont dimensionnées pour votre site, et celle qui correspond le mieux à votre priorité est recommandée. Seule la capacité pilotable compte dans la capacité ferme.",
    "landing.method.s4.t": "Coûts, permis, planning",
    "landing.method.s4.d": "CAPEX, OPEX et LCOE indicatifs (±\u00a0fourchette), réglementation par pays, calendrier des jalons.",
    "landing.pricing.desc": "Les packs sont des crédits prépayés, utilisables pendant 12\u00a0mois et décomptés un rapport à la fois. Chaque rapport couvre le même périmètre complet.",
    "landing.pricing.first.overline": "Analyse gratuite",
    "landing.pricing.first.price": "Gratuit",
    "landing.pricing.first.desc": "Dimensionnement automatique approximatif et fourchette de coûts indicative. Tout projet. Aucun achat requis.",
    "landing.pricing.first.b1": "✓ Secteur + pays + charge en 7\u00a0étapes",
    "landing.pricing.first.b2": "✓ Mix de production (solaire / batterie / diesel / gaz)",
    "landing.pricing.first.b3": "✓ Vérification de la capacité ferme + part renouvelable",
    "landing.pricing.first.b4": "✓ Fourchette de CAPEX (basse · attendue · haute)",
    "landing.pricing.single.overline": "Rapport détaillé",
    "landing.pricing.single.badge": "Le plus choisi",
    "landing.pricing.single.desc": "Paiement unique · un rapport complet.",
    "landing.pricing.single.b1": "✓ Modèle énergétique complet + liste d'équipements",
    "landing.pricing.single.b2": "✓ Détail CAPEX / OPEX + LCOE",
    "landing.pricing.single.b3": "✓ Cartographie réglementaire par pays",
    "landing.pricing.single.b4": "✓ PDF de faisabilité + export CSV",
    "landing.pricing.single.b5": "✓ Lien de partage pour votre équipe",
    "landing.pricing.single.cta": "Obtenir un rapport détaillé",
    "landing.pricing.bundle.overline": "Pack · {n}\u00a0rapports",
    "landing.pricing.bundle.badge": "Recommandé",
    "landing.pricing.bundle.per_report": "/rapport",
    "landing.pricing.bundle.save": " · économisez {amount}",
    "landing.pricing.bundle.b1": "✓ {n}\u00a0crédits de rapport prépayés",
    "landing.pricing.bundle.b2": "✓ À utiliser sous 12\u00a0mois",
    "landing.pricing.bundle.b3": "✓ Déverrouillage en un clic par projet",
    "landing.pricing.bundle.b4": "✓ Facture consolidée",
    "landing.pricing.bundle.cta": "Acheter le pack",
    "landing.pricing.ent.overline": "Sur mesure / Entreprise",
    "landing.pricing.ent.desc": "Fixé selon le volume de rapports et le nombre d'utilisateurs · tarif logiciel par rapport dégressif.",
    "landing.pricing.ent.b1": "✓ Abonnement logiciel annuel ou solde de crédits prépayés",
    "landing.pricing.ent.b2": "✓ Tarif par rapport dégressif selon le volume",
    "landing.pricing.ent.b3": "✓ Pour les équipes internes qui cadrent de nombreux projets",
    "landing.pricing.ent.b4": "✓ Accès pour les membres de votre équipe",
    "landing.pricing.ent.cta": "Parler à un responsable de compte",
    "landing.pricing.ext.overline": "Accompagnement étendu",
    "landing.pricing.ext.price": "Sur arrangement",
    "landing.pricing.ext.desc": "Poursuivez le travail au-delà du rapport automatisé.",
    "landing.pricing.ext.b1": "✓ Prolongez un cadrage enregistré au-delà du résultat automatisé",
    "landing.pricing.ext.b2": "✓ Discuté séparément de la tarification logicielle",
    "landing.pricing.ext.b3": "✓ Les crédits de rapport sont déduits de la mission",
    "landing.cta.title": "Cadrez plus de projets, plus vite.",
    "landing.cta.body": "L'analyse gratuite ne coûte rien — vous ne payez qu'au déverrouillage du rapport logiciel complet. Achetez des packs pour réduire le tarif par rapport.",
    // Configurator
    "wiz.title": "Configurez votre projet",
    "wiz.overline": "Analyse gratuite",
    "wiz.step": "Étape",
    "wiz.of": "sur",
    "wiz.next": "Suivant",
    "wiz.back": "Précédent",
    "wiz.generate": "Générer l'analyse",
    "wiz.running": "Calcul en cours…",
    "wiz.step.1": "Secteur & application",
    "wiz.step.2": "Localisation",
    "wiz.step.3": "Charge & demande",
    "wiz.step.4": "Site & ambiant",
    "wiz.step.5": "Situation réseau",
    "wiz.step.6": "Mode de détention",
    "wiz.step.7": "Priorité",
    "wiz.priority.title": "Priorité",
    "wiz.lf.title": "Préréglages du facteur de charge",
    "wiz.load.derived": "Déduit de la pointe et de l'énergie journalière (énergie journalière ÷ (pointe × 24)).",
    "wiz.load.err_above": "L'énergie journalière dépasse la pointe × 24 h. Vérifiez la pointe et l'énergie journalière, ainsi que leurs unités (kW ou MW, kWh ou MWh).",
    "wiz.load.err_below": "L'énergie journalière est inférieure à une heure à la pointe. Vérifiez la pointe et l'énergie journalière, ainsi que leurs unités (kW ou MW, kWh ou MWh).",
    "wiz.load.warn_peaky": "Charge inhabituellement pointue — vérifiez vos saisies.",
    "wiz.lf.hint": "Ils règlent le facteur de charge — le rapport entre la demande moyenne et la pointe. La courbe sur 24 h provient de votre secteur.",
    "wiz.lf.p80": "Facteur de charge 0,80",
    "wiz.lf.p80.sub": "Demande moyenne ≈ 80 % de la pointe — exploitation continue",
    "wiz.lf.p45": "Facteur de charge 0,45",
    "wiz.lf.p45.sub": "Demande moyenne ≈ 45 % de la pointe — exploitation de jour",
    "wiz.lf.p35": "Facteur de charge 0,35",
    "wiz.lf.p35.sub": "Demande moyenne ≈ 35 % de la pointe — charge variable ou saisonnière",
    "wiz.priority.sub": "Comment le configurateur doit-il départager les options ?",
    "wiz.priority.lowest_upfront": "Coût d'investissement minimal",
    "wiz.priority.lowest_lifetime": "Coût total sur cycle de vie minimal",
    "wiz.priority.max_reliability": "Fiabilité maximale",
    "wiz.priority.lowest_emission": "Émissions minimales",
    "wiz.priority.banner": "Une fois l'analyse générée, nous afficherons le mix recommandé et la fourchette de coûts. Le détail complet, la checklist d'autorisations, le PDF du mémorandum et le plan financier sont dans le rapport payant.",
    "wiz.gas_avail.title": "Disponibilité du gaz",
    "wiz.gas_avail.hint": "Choisissez votre situation d'approvisionnement. Si vous n'êtes pas sûr, sélectionnez « inconnu » — nous continuerons à chiffrer les candidats au gaz, mais la recommandation sera signalée comme conditionnée à un approvisionnement confirmé.",
    "wiz.gas_avail.pipeline": "Gaz par pipeline disponible",
    "wiz.gas_avail.vp": "Pipeline virtuel (GNL / GNC)",
    "wiz.gas_avail.none": "Aucun gaz disponible",
    "wiz.gas_avail.unknown": "Inconnu / à confirmer",
    "wiz.diesel_avail.title": "Disponibilité du diesel",
    "wiz.diesel_avail.hint": "Le diesel peut-il être livré et stocké sur site ? « Disponible » suppose des livraisons selon les besoins, même si l'approvisionnement est irrégulier. Choisissez « démarrage autonome uniquement » si le diesel ne peut servir qu'à démarrer une centrale à gaz, ou « aucun » si le site ne peut pas utiliser de diesel. Dans les deux cas, la recommandation se limite aux centrales à gaz.",
    "wiz.diesel_avail.available": "Disponible (livraisons + stockage sur site)",
    "wiz.diesel_avail.constrained": "Démarrage autonome uniquement (pas d'approvisionnement régulier)",
    "wiz.diesel_avail.none": "Aucun (le site ne peut pas utiliser de diesel)",
    "wiz.thermal.title": "Avancé : thermique & gaz (optionnel)",
    "wiz.thermal.sub": "Pour les projets utilities, miniers et industriels · laisser vide pour un dimensionnement solar-first",
    "wiz.disclaimer": "Le Power Configurator fournit des résultats indicatifs, de niveau préliminaire, à titre informatif uniquement. Il ne constitue pas un conseil d'ingénierie, financier ou d'investissement.",
    // Results
    "results.tag": "Indicatif · Niveau préliminaire",
    "results.title": "Cadrage de première passe",
    "results.reconfigure": "Reconfigurer",
    "results.save": "Enregistrer",
    "results.saved": "Enregistré",
    "results.saving": "Enregistrement…",
    "results.talk": "Contacter notre équipe",
    "results.priority_label": "Priorité :",
    "results.priority.lowest_upfront": "coût d'invest. min.",
    "results.priority.lowest_lifetime": "coût cycle de vie min.",
    "results.priority.max_reliability": "fiabilité max.",
    "results.priority.lowest_emission": "émissions min.",
    "results.block.1": "Modèle énergétique optimisé",
    "results.block.1_sub": "Mix recommandé",
    "results.free_preview": "Aperçu gratuit",
    "results.annual_energy": "Énergie annuelle servie :",
    "results.annual_demand": "Demande annuelle :",
    "results.annual_served": "Énergie annuelle servie :",
    "results.annual_generation": "Génération annuelle :",
    "results.renewable_fraction": "Part renouvelable :",
    "results.tile.solar": "Solaire PV",
    "results.tile.battery": "Batterie",
    "results.tile.diesel": "Groupes diesel",
    "results.tile.diesel_blackstart": "Diesel de démarrage (secours)",
    "results.tile.ocgt": "Turbines à gaz (OCGT)",
    "results.tile.ccgt": "Cycle combiné (CCGT)",
    "results.tile.wind": "Éolien",
    "results.tile.hfo": "HFO",
    "results.tile.gas": "Moteurs à gaz",
    "results.tile.firm": "Capacité ferme",
    "results.tile.derate": "Déclassement des groupes",
    "results.block.1b": "Votre modèle recalculé",
    "results.block.1b_title": "Votre configuration",
    "results.adjusted_mix": "Mix ajusté",
    "results.adjusted.delta_basis": "Les écarts (Δ) sont mesurés par rapport à la centrale recommandée :",
    "results.adjusted.delta_basis_generic": "Les écarts (Δ) sont mesurés par rapport à la configuration recommandée.",
    "results.thermal.overline": "Tout-thermique en base · scénario alternatif",
    "results.thermal.title": "Tout-thermique en base — scénario alternatif",
    "results.thermal.sub": "Conception gaz-seul autonome dimensionnée à la pointe × 1,20. À comparer à la centrale recommandée ci-dessus, sur le CAPEX initial et sur le coût sur la durée de vie.",
    "results.thermal.screening": "Préliminaire",
    "results.thermal.capex": "CAPEX thermique",
    "results.thermal.fuel_cost": "Coût carburant annuel",
    "results.thermal.generation": "Génération annuelle",
    "results.thermal.cod": "Délai équipement + construction",
    "results.thermal.gas_engine": "Moteur à gaz",
    "results.thermal.derated": "après déclassement",
    "results.thermal.mo": "mois",
    "results.thermal.gas_unconfirmed": "Approvisionnement en gaz non confirmé — coûts calculés à {price} livré",
    "thermal.gate.disabled": "Désactivé par l’administrateur.",
    "thermal.gate.below_min": "La pointe du site ({peak}) est inférieure au minimum de {min} pour cette technologie.",
    "results.thermal.build": "construction",
    "results.thermal.water": "eau requise",
    "results.thermal.chp_recovered": "chaleur récupérée",
    "results.thermal.lead": "Délai",
    "results.thermal.total_capex": "CAPEX total (tout compris)",
    "results.thermal.lcoe": "LCOE",
    "results.thermal.opex": "OPEX annuel",
    "results.thermal.firm_capacity": "Capacité après déclassement",
    "results.thermal.scenario_pill": "Scénario alternatif",
    "results.thermal.full_thermal_recommended": "Recommandation tout thermique",
    "results.thermal.suppressed_reason": "La solution recommandée ne comporte pas de capacité renouvelable au-delà de la part minimale : il s'agit d'une recommandation tout thermique. Aucune comparaison n'est présentée, les deux scénarios étant la même centrale.",
    "results.thermal.compare_title": "Comparaison des scénarios",
    "results.thermal.compare_two_plants": "Il s'agit de deux centrales différentes : la recommandation du configurateur et la centrale thermique sélectionnée à l'étape 7.",
    "results.thermal.compare_sub": "Les deux scénarios sont dimensionnés à la même pointe (× 1,20 en capacité ferme). Le scénario préféré pour l'approvisionnement pilote le PDF payant.",
    "results.thermal.hybrid": "Centrale recommandée",
    "results.thermal.baseload": "Tout-thermique en base",
    "results.thermal.buyer_selection": "Votre sélection thermique",
    "results.thermal.metric_capex": "CAPEX tout compris",
    "results.thermal.metric_lcoe": "LCOE",
    "results.thermal.metric_annual_opex": "OPEX annuel",
    "results.thermal.metric_renewable": "Part renouvelable",
    "results.thermal.preferred_flag": "Préféré pour le rapport",
    "results.thermal.set_preferred": "Marquer préféré",
    "results.thermal.preferred_note": "Ce scénario sera mis en avant sur votre rapport PDF payant comme voie d'approvisionnement préférée par l'acheteur.",
    "results.thermal.npv_title": "Coût total sur 20 ans (TCO)",
    "results.thermal.npv_sub": "Valeur actuelle à {rate} % nominal (≈ {real} % réel) du CAPEX, du carburant / O&M / réseau indexés à {esc} %/an, de l'éventuel remplacement de batterie et des révisions, sur 20 ans — chiffre du configurateur.",
    "results.thermal.npv_years": "20 ans",
    "results.thermal.emissions_title": "CO₂ opérationnel / an",
    "results.thermal.emissions_sub": "Émissions directes de la production dispatchée. Le mix recommandé est calculé sur ce qu'il dispatche réellement — moteurs à gaz, diesel ou réseau ; thermique-seul = turbines/moteurs à gaz nets du crédit CHP.",
    "results.thermal.tonnes_yr": "t CO₂/an",
    "results.thermal.csv_download": "Télécharger le CSV de comparaison",
    "results.thermal.csv_pending": "Préparation…",
    "results.thermal.gas_slider_label": "Prix du gaz =",
    "results.thermal.gas_slider_preview": "Aperçu",
    "results.thermal.gas_slider_authoritative": "En direct",
    "results.thermal.gas_slider_recalculating": "Recalcul…",
    "results.thermal.gas_reset": "Réinitialiser",
    "results.thermal.preset_gulf": "Golfe",
    "results.thermal.preset_wafrica": "moy. Afrique O.",
    "results.thermal.preset_lng": "GNL spot",
    "results.thermal.notes_label": "Justification acheteur pour prêteur / achat",
    "results.thermal.notes_placeholder": "ex. Préférence thermique complet — gaz pipeline bon marché, pas d'espace toiture pour le solaire, besoin de dispatch ferme pour four à arc.",
    "results.thermal.notes_char_count": "car.",
    "results.thermal.notes_max": "500 max",
    "results.thermal.save_compare_pdf": "Enregistrer la comparaison en PDF",
    "results.thermal.save_compare_pdf_pending": "Création du PDF…",
    "results.tte.title": "Parlez à un ingénieur",
    "results.tte.body": "Ce projet est sur mesure. Vous souhaitez aller plus loin\u00a0? Notre équipe d'ingénierie peut dimensionner une architecture au-delà des options standard, mener une étude de faisabilité bancable et vous conseiller sur les achats et l'EPC.",
    "results.tte.cta": "Écrire à un ingénieur",
    "results.tte.email": "engineering@futureenergyventure.com",
    "results.back_to_priority": "Retour",
    "results.back_to_priority_tip": "Retour à l'étape Priorité — vos entrées sont conservées",
    "results.reconfirm_title": "Reconstruire le projet depuis le début ?",
    "results.reconfirm_body": "Toutes les entrées actuelles (site, charge, priorité, thermique) seront réinitialisées. Pour des ajustements mineurs sans repartir de zéro, utilisez la flèche Retour.",
    "results.reconfirm_confirm": "Repartir de zéro",
    "results.reconfirm_cancel": "Annuler",
    "results.firm_shortfall_overline": "Capacité ferme insuffisante",
    "results.firm_shortfall_title": "Le mix recommandé ne respecte pas la marge de réserve de 20 %",
    "results.firm_shortfall_body": "Capacité ferme disponible ({available} kW) sous la cible de {target} kW (pointe {peak} kW × 1,20). Ajoutez des unités, augmentez la taille unitaire, ou acceptez une marge de réserve plus faible avant la phase de faisabilité.",
    "results.unmet_load_overline": "Énergie non fournie dans le temps",
    "results.unmet_load_title": "Le dispatch laisse un déficit d'énergie pendant les périodes de faible solaire / batterie vide",
    "results.unmet_load_body": "Le dispatch de screening indique environ {unmet_mwh} MWh/an ({pct}% de la demande annuelle) que le dimensionnement actuel ne peut pas couvrir — les heures où les sources disponibles de la centrale ne prennent pas la charge complète. Distinct de la capacité ferme (kW au pic) : ceci est de l'énergie dans le temps (kWh). Ajoutez de la capacité ferme ou de stockage, ou planifiez un délestage contrôlé.",
    "results.cond_fuel_overline": "Approvisionnement en gaz — confirmation requise",
    "results.cond_fuel_title": "Sous réserve d'un approvisionnement en gaz confirmé — le mix recommandé suppose du gaz disponible par pipeline ou pipeline virtuel",
    "results.cond_fuel_body": "Vous avez sélectionné « inconnu » pour la disponibilité du gaz. Cette présélection conserve les options au gaz dans la comparaison, mais l'architecture recommandée, ainsi que son CAPEX, son LCOE et ses émissions de CO₂, dépend d'un approvisionnement ferme. Confirmez la capacité du pipeline ou un contrat de pipeline virtuel (GNL / GNC) avant de passer à l'étude de faisabilité.",
    "results.firm_buildup_title": "Décomposition de la capacité ferme (ce que le dispatch a réellement vu)",
    "results.firm_dispatchable_kw": "Capacité dispatchable (groupes électrogènes + moteurs à gaz + turbines)",
    "results.firm_battery_derated_kw": "Batterie comptée en capacité ferme",
    "results.firm_battery_rated_kw": "Batterie — puissance nominale",
    "results.firm_available_kw": "Capacité ferme disponible",
    "results.firm_target_kw": "Cible ferme (pointe × 1,20)",
    "results.firm_low_sun_kw": "Cas de stress (batterie à la charge détenue avant la fenêtre de pointe de 4 h)",
    "results.firm_low_sun_kw_nobatt": "Cas de stress (capacité pilotable seule)",
    "results.firm_status_green": "CAPACITÉ FERME OK",
    "results.firm_status_amber": "FERME NON ATTEINT — ÉNERGIE NON SERVIE > 0",
    "results.firm_status_red": "DÉFAUT DE CAPACITÉ FERME EN STRESS FAIBLE-SOLEIL",
    "results.firm_status_n1_fail": "FERME OK — DÉFAUT N-1 : À L’ARRÊT DU PLUS GROS GROUPE, LE SITE EST EN DÉFICIT",
    "results.config_saved": "Votre configuration a été enregistrée sur ce projet.",
    "results.stale_overrides_overline": "Modifications enregistrées — restauration partielle",
    "results.stale_overrides_title": "{n} de vos modifications ne s'appliquent plus",
    "results.stale_overrides_footer": "Le configurateur actuel n'a pas pu honorer ces modifications (valeur bornée, technologie retirée ou repère modifié). Tout le reste a été restauré. Recalculez pour figer les nouvelles valeurs.",
    "results.stale_overrides_dismiss": "Ignorer",
    "results.thermal_adj.overline": "Votre configuration thermique",
    "results.thermal_adj.title": "Gaz & thermique ajustés",
    "results.thermal_adj.sub": "Les badges Δ comparent chaque métrique au mix thermique recommandé ci-dessus.",
    "results.thermal_adj.pill": "Ajusté",
    "results.thermal.cf": "Facteur de charge",
    "results.thermal.total_capex_allin": "CAPEX total (tout compris)",
    "results.thermal.cf_line": "Facteur de charge {cf} % — fonctionne en moyenne à {cf} % de la capacité installée",
    "results.thermal.serves_line": "Couvre {pct} % de la charge du site",
    "results.thermal.partload_line": "Rendement moyen η {eta} % — {share} % des heures de marche sous 60 % de charge unitaire",
    "results.thermal.partload_line_eta": "Rendement moyen η {eta} %",
    "results.thermal.partload_line_diesel": "Consommation moyenne {lpk} l/kWh — {share} % des heures de marche sous 60 % de charge unitaire",
    "results.thermal.partload_line_diesel_lpk": "Consommation moyenne {lpk} l/kWh",
    "results.thermal_editor.oversize": "Ce banc représente {mult}× l’objectif de capacité ferme de {target} kW — vérifiez le nombre et la taille des unités.",
    "results.thermal.use_plant": "Utiliser cette centrale dans ma configuration",
    "results.thermal.use_plant_note": "Remplace votre configuration par cette centrale ; Réinitialiser revient à la recommandation.",

    "results.thermal_adj.scope_in_mix": "Inclus dans « Votre configuration » : ces unités font partie de la centrale recalculée.",
    "results.thermal_adj.scope_separate": "Scénario distinct — ces unités thermiques ne font pas partie de « Votre configuration » ci-dessus.",
    "results.thermal_adj.unmet": "Demande non servie :",
    "results.range.title": "Fourchette CAPEX indicative",
    "results.range.central": "Estimation centrale",
    "results.range.region_mult": "Multiplicateur région",
    "results.range.low": "bas",
    "results.range.central_short": "central",
    "results.range.high": "haut",
    "results.carbon.overline": "Empreinte carbone estimée",
    "results.carbon.unit": "t CO₂/an",
    "results.carbon.intensity": "Intensité",
    "results.carbon.intensity_unit": "gCO₂/kWh",
    "results.carbon.recommended_mix": "Mix recommandé",
    "results.carbon.scale_low": "0 — bas",
    "results.carbon.scale_med": "200 — moyen",
    "results.carbon.scale_high": "500 — élevé",
    "results.carbon.your_config": "Votre configuration",
    "results.carbon.rating.low": "BAS",
    "results.carbon.rating.medium": "MOYEN",
    // Results page FR (a07c)
    "pdf.unavailable": "Votre rapport n'a pas pu être généré pour le moment. Veuillez réessayer dans quelques minutes.",
    "pdf.download_failed": "Impossible de télécharger le rapport",
    "results.head.peak": "Pointe",
    "results.head.firm_target": "Cible ferme",
    "frontier.title": "Ce que vous obtiendriez pour chaque priorité — même site, quatre objectifs",
    "frontier.yours": "Votre choix",
    "frontier.pinned_h": "Votre configuration figée",
    "frontier.pinned_body": "Hors classement — cette centrale a été fixée par vous ; elle n'est donc pas classée face aux autres options.",
    "frontier.floor_title": "Dimensionnement limité par le plancher d'un composant, non optimisé",
    "frontier.floor": "au plancher",
    "frontier.tco": "Coût total 20 ans",
    "frontier.aria": "Meilleure option pour chaque priorité",
    "footer.terms": "Conditions d'utilisation",
    "footer.privacy": "Politique de confidentialité",
    "footer.refund": "Politique de remboursement",
    // How it works + Field help (Prompt L)
    "how.title": "Comment fonctionne le Power Configurator",
    "how.show": "Afficher",
    "how.hide": "Masquer",
    "how.summary": "Nous dimensionnons plusieurs options de centrale pour votre site, ne retenons que celles qui couvrent votre demande de façon fiable et recommandons celle qui correspond le mieux à votre priorité. Les résultats sont comparés à ce que vous feriez sans le projet : une centrale 100 % diesel hors réseau, l'alimentation réseau avec un secours diesel sur un réseau faible, ou l'alimentation réseau si votre raccordement est fiable.",
    "how.b1.title": "1 · Ce que fait l'outil",
    "how.b1.body": "À partir de votre charge, de votre localisation, de votre situation réseau et des conditions du site, le configurateur construit plusieurs options de centrale — solaire, batteries, groupes diesel ou gaz et réseau — et dimensionne chacune pour votre site. Chaque option doit offrir une capacité ferme d'au moins 120 % de votre pointe, à partir de sources disponibles à toute heure : groupes, batteries et, s'il est fiable, le réseau.",
    "how.b2.title": "2 · Comment la recommandation est choisie",
    "how.b2.body": "Parmi les options qui passent ce contrôle de fiabilité, il recommande celle qui répond le mieux à la priorité choisie à la dernière étape : coût d'investissement minimal, coût total sur cycle de vie minimal, fiabilité maximale ou émissions minimales. « Meilleure » signifie donc meilleure pour votre priorité — si vous changez de priorité, la recommandation peut changer.",
    "how.b3.title": "3 · À quoi le projet est comparé",
    "how.b3.intro": "Chaque économie et chaque rendement sont mesurés par rapport à ce que vous feriez sans le projet. Cela dépend de votre situation réseau :",
    "how.b3.off_grid.t": "Hors réseau",
    "how.b3.off_grid.d": "une centrale 100 % diesel dimensionnée pour la même charge (investissement, carburant et maintenance).",
    "how.b3.weak_grid.t": "Réseau faible",
    "how.b3.weak_grid.d": "l'alimentation réseau plus des groupes diesel de secours pour couvrir les coupures.",
    "how.b3.on_grid.t": "Sur réseau (fiable)",
    "how.b3.on_grid.d": "l'achat de la même énergie au réseau au tarif national. Les coûts de raccordement ne sont pas inclus.",
    "how.b3.none.t": "Pas de comparaison",
    "how.b3.none.d": "si vous ajustez vous-même le mix, ou si un site hors réseau ne peut utiliser le diesel que pour le démarrage autonome, ou pas du tout. La centrale est quand même dimensionnée et chiffrée, mais aucune économie, aucun délai de récupération ni aucun rendement n'est affiché.",
    "how.b3.yours": "Votre choix",
    "how.b4.title": "4 · Comment sont calculés les économies, la VAN et le TRI",
    "how.b4.body": "Économies annuelles = coût d'exploitation annuel du cas de comparaison (carburant, factures d'électricité, maintenance) moins celui de la centrale recommandée — il ne s'agit donc pas uniquement d'économies de carburant. Le résultat gratuit affiche un délai de récupération indicatif ; le rapport complet ajoute la VAN et le TRI : l'investissement total de la centrale est remboursé par ces économies sur 20 ans, avec un taux d'actualisation de 12 % (par défaut), des économies qui augmentent de 3,5 % par an et, si la centrale comporte des batteries, leur remplacement la 10e année.",
    "how.use.title": "Comment utiliser l'outil",
    "how.use.body": "Complétez les 7 étapes — la plupart des champs sont pré-remplis pour votre pays : vous pouvez générer une première analyse avec les valeurs par défaut et l'affiner ensuite. Survolez ou touchez le ⓘ à côté de chaque champ pour voir ce qu'il signifie et son effet sur le résultat. L'analyse gratuite présente le mix recommandé, ses coûts et un délai de récupération indicatif ; les économies, la VAN et le TRI figurent dans le rapport complet.",
    "how.scope": "Chiffres indicatifs de niveau préliminaire, pour décider si un projet mérite d'être approfondi — ni une étude d'ingénierie, ni un conseil en investissement.",
    "how.results_link": "Comment ces résultats sont calculés",
    "help.aria": "Plus d'informations :",
    "help.sector": "Définit le profil de charge type sur 24 h de votre site et un facteur de charge de départ (Industrie 0,70, Mines 0,85, Électricité 0,60, Télécoms 0,75). Vous pourrez modifier le facteur de charge à l'étape 3.",
    "help.application": "Décrit votre site dans les résultats et le rapport. Il ne modifie pas le dimensionnement — le profil de charge provient du secteur et des valeurs saisies à l'étape 3.",
    "help.country": "Définit le niveau de coûts régional (multiplicateur de coût), l'ensoleillement, le prix du carburant, le tarif réseau et les heures de coupure par défaut, ainsi que les règles d'autorisation présentées dans le rapport. Les valeurs pré-remplies peuvent être modifiées aux étapes suivantes.",
    "help.project_name": "Facultatif. Sert uniquement à nommer le projet dans Mes projets et sur le rapport.",
    "help.peak_kw": "La puissance maximale appelée par votre site à un instant donné, en kW. Chaque option doit fournir une capacité ferme d'au moins 120 % de cette valeur — la cible s'affiche sous le champ.",
    "help.day_kwh": "L'énergie totale consommée par votre site sur une journée type, en kWh — d'après vos factures ou vos relevés si vous les avez. Laissez 0 et nous la calculons : pointe × facteur de charge × 24 h. Si vous saisissez les deux, gardez-les cohérents avec le facteur de charge.",
    "help.lf_presets": "Raccourcis qui règlent le facteur de charge ci-dessous. Choisissez celui qui correspond le mieux au fonctionnement de votre site.",
    "help.load_factor": "Demande moyenne divisée par la demande de pointe. 0,80 : le site fonctionne près de sa pointe 24 h/24 ; 0,35 : des pointes courtes et une moyenne faible. Il détermine l'énergie consommée, donc le solaire, le stockage et le carburant nécessaires.",
    "help.altitude": "Altitude du site. Les groupes perdent environ 4 % de leur puissance par tranche de 300 m au-delà de 1 000 m ; nous les surdimensionnons pour compenser.",
    "help.ambient": "La température la plus élevée à laquelle la centrale doit fonctionner. Les groupes perdent environ 2 % de leur puissance par tranche de 5 °C au-delà de 40 °C. Sur les sites très chauds et ensoleillés, chaque panneau solaire produit aussi un peu moins.",
    "help.irradiance": "Énergie solaire moyenne reçue par jour sur une surface horizontale, pré-remplie pour votre pays. Plus elle est élevée, plus chaque kW solaire produit d'énergie. Remplacez-la par des données mesurées ou satellitaires (p. ex. Global Solar Atlas) si vous en disposez.",
    "help.grid": "Votre raccordement au réseau aujourd'hui. Il détermine la référence de comparaison du projet et les sources prises en compte dans la capacité ferme.",
    "help.grid.on_grid.d": "le réseau alimente le site. Comparé à l'achat de la même énergie au tarif national.",
    "help.grid.weak_grid.d": "raccordé, avec des coupures régulières. Comparé à l'alimentation réseau plus un secours diesel.",
    "help.grid.off_grid.d": "pas de réseau. Comparé à une centrale 100 % diesel.",
    "help.outage_hours": "Nombre moyen d'heures par jour sans électricité du réseau, enregistré avec votre projet. La comparaison utilise notre estimation par pays des heures de coupure. Le rapport le précise, et vous pourrez la remplacer par vos données mesurées au stade de la faisabilité.",
    "help.fuel_price": "Prix du diesel livré sur site, en USD par litre, pré-rempli pour votre pays. Il fixe le coût du carburant des groupes de la centrale et du cas de comparaison diesel : il a donc un effet important sur les économies, et un prix plus élevé raccourcit généralement le délai de récupération.",
    "help.ownership": "Qui possédera et exploitera la centrale. Ce choix détermine les autorisations et licences listées pour votre pays. Dans cette version, le modèle financier applique la même structure de financement indicative à tous les modes de détention.",
    "help.own.captive.d": "vous possédez la centrale et consommez l'électricité sur site.",
    "help.own.IPP.d": "un producteur indépendant vend l'électricité au réseau ou à un distributeur.",
    "help.own.BOO.d": "un développeur construit, possède et exploite la centrale et vous vend l'électricité.",
    "help.own.PPP.d": "un partenariat entre une entité publique et une entreprise privée.",
    "help.own.ESCO.d": "une société de services énergétiques fournit l'électricité ou les économies dans le cadre d'un contrat de long terme.",
    "help.own.lease.d": "vous louez les équipements contre un loyer fixe.",
    "help.priority": "Ce qui compte le plus pour vous. Seules les options qui passent le contrôle de fiabilité sont retenues ; ce choix détermine laquelle est recommandée.",
    "help.prio.lowest_upfront": "l'investissement le plus faible aujourd'hui. Les options dont le coût sur 20 ans dépasse le double de la moins chère sont écartées.",
    "help.prio.lowest_lifetime": "le coût total le plus bas sur 20 ans : investissement plus carburant, maintenance et factures réseau, actualisés.",
    "help.prio.max_reliability": "l'option la moins chère sur 20 ans qui couvre 120 % de votre pointe en capacité ferme sans aucune demande non servie.",
    "help.prio.lowest_emissions": "le CO₂ par kWh le plus bas, à condition que son coût sur 20 ans ne dépasse pas de plus de 25 % celui de l'option la moins chère.",
    "help.gas_avail": "Les centrales à cycle combiné (CCGT) nécessitent du gaz par pipeline. Un pipeline virtuel (GNL ou GNC livré par camion) convient aux moteurs à gaz et aux turbines à cycle ouvert. « Aucun gaz disponible » exclut les centrales au gaz ; « Inconnu » les conserve mais signale le résultat comme conditionné à la confirmation de l'approvisionnement.",
    "help.thermal.units": "Facultatif. Indiquez le nombre d'unités et la taille de chacune (MW) pour tester votre propre centrale thermique. Elle est chiffrée comme un scénario thermique présenté à côté de la recommandation. Laissez le nombre à 0 pour l'ignorer.",
    "help.thermal.chp": "Cogénération : récupère environ 40 à 50 % de l'énergie du combustible sous forme de chaleur utile (chaleur de process, eau chaude ou froid par absorption). Intéressant seulement si le site a un besoin de chaleur régulier.",
    "help.thermal.chp_host": "L'unité thermique sur laquelle est installé l'équipement de récupération de chaleur.",
    "help.thermal.heat_demand": "Chaleur moyenne que le site peut réellement utiliser, en kW thermiques.",
    "help.thermal.gas_source": "Mode d'arrivée du gaz sur le site. Le gaz par pipeline est généralement le moins cher ; le GNL, le GNC et le GPL sont acheminés par navire ou camion et coûtent plus cher par GJ.",
    "help.thermal.gas_available": "Cochez uniquement si l'approvisionnement en gaz du site est confirmé.",
    "help.thermal.gas_price": "Prix du gaz livré sur site, en USD par gigajoule. Pour convertir depuis des USD/MMBtu, divisez par 1,055.",
    "help.thermal.ambient": "Température et altitude du site utilisées pour déclasser les unités thermiques. Les turbines à gaz perdent davantage de puissance que les moteurs à gaz par forte chaleur et en altitude.",
    "help.thermal.derate": "Réduit la puissance des unités thermiques selon la température et l'altitude du site. Laissez coché sauf si vous disposez des puissances constructeur spécifiques au site.",
    "wiz.ownership.sub": "Détermine les autorisations et licences listées pour votre pays.",

    // Wizard labels (Prompt M)
    "wiz.sector.sub": "Quel secteur décrit le mieux l'acheteur\u00a0?",
    "wiz.location.sub": "Le pays détermine la région de coûts, les conditions ambiantes par défaut, les hypothèses réseau et les règles d'autorisation.",
    "wiz.load.sub": "Indiquez la demande de pointe (kW) et/ou l'énergie journalière (kWh/jour). Si vous n'en saisissez qu'une, nous déduisons l'autre à partir du facteur de charge.",
    "wiz.site.sub": "Pré-rempli avec les valeurs par défaut du pays choisi ({{country}}). Modifiez-les si vous disposez de données mesurées.",
    "wiz.grid.sub": "Détermine les technologies prises en compte dans la capacité ferme.",
    "wiz.sector.industrial": "Industrie",
    "wiz.sector.mining": "Mines",
    "wiz.sector.utilities": "Électricité",
    "wiz.sector.telecom": "Télécoms",
    "wiz.application.label": "Application",
    "wiz.application.placeholder": "Choisissez une application…",
    "wiz.app.manufacturing": "Usine de fabrication",
    "wiz.app.warehouse": "Entrepôt / logistique",
    "wiz.app.agro": "Agro-transformation",
    "wiz.app.cement": "Cimenterie / industrie lourde",
    "wiz.app.cold_chain": "Chaîne du froid / agroalimentaire",
    "wiz.app.open_pit": "Mine à ciel ouvert",
    "wiz.app.underground": "Mine souterraine",
    "wiz.app.processing": "Usine de traitement",
    "wiz.app.camp": "Base vie & bureaux",
    "wiz.app.exploration": "Site d'exploration",
    "wiz.app.ipp": "Producteur indépendant d'électricité (IPP)",
    "wiz.app.distribution": "Société de distribution d'électricité",
    "wiz.app.minigrid": "Opérateur de mini-réseau",
    "wiz.app.grid_renewable": "Centrale renouvelable raccordée au réseau",
    "wiz.app.macro_tower": "Site macro (pylône)",
    "wiz.app.aggregation": "Site d'agrégation régional",
    "wiz.app.data_centre": "Centre de données / edge",
    "wiz.app.fibre_hub": "Nœud de réseau fibre",
    "wiz.app.other": "Autre",
    "wiz.app_other.label": "Décrivez l'application",
    "wiz.app_other.placeholder": "ex. briqueterie",
    "wiz.country.label": "Pays",
    "wiz.country.region": "Région\u00a0:",
    "wiz.country.cost_mult": "Multiplicateur de coût\u00a0:",
    "wiz.project_name.label": "Nom du projet (facultatif)",
    "wiz.project_name.placeholder": "ex. cimenterie de Kaduna",
    "region.West Africa": "Afrique de l'Ouest",
    "region.East Africa": "Afrique de l'Est",
    "region.Central Africa": "Afrique centrale",
    "region.Southern Africa": "Afrique australe",
    "region.North Africa": "Afrique du Nord",
    "country.Algeria": "Algérie",
    "country.Angola": "Angola",
    "country.Benin": "Bénin",
    "country.Botswana": "Botswana",
    "country.Burkina Faso": "Burkina Faso",
    "country.Cameroon": "Cameroun",
    "country.Chad": "Tchad",
    "country.Cote d'Ivoire": "Côte d'Ivoire",
    "country.DRC": "RDC",
    "country.Egypt": "Égypte",
    "country.Ethiopia": "Éthiopie",
    "country.Gabon": "Gabon",
    "country.Ghana": "Ghana",
    "country.Guinea": "Guinée",
    "country.Guinea-Bissau": "Guinée-Bissau",
    "country.Kenya": "Kenya",
    "country.Liberia": "Libéria",
    "country.Mali": "Mali",
    "country.Mauritania": "Mauritanie",
    "country.Morocco": "Maroc",
    "country.Mozambique": "Mozambique",
    "country.Namibia": "Namibie",
    "country.Niger": "Niger",
    "country.Nigeria": "Nigeria",
    "country.Rwanda": "Rwanda",
    "country.Senegal": "Sénégal",
    "country.South Africa": "Afrique du Sud",
    "country.Tanzania": "Tanzanie",
    "country.Togo": "Togo",
    "country.Uganda": "Ouganda",
    "country.Zambia": "Zambie",
    "wiz.peak.label": "Demande de pointe (kW)",
    "wiz.peak_chip.peak": "Pointe",
    "wiz.peak_chip.target": "→ capacité ferme cible (pointe × 1,20)",
    "wiz.day_kwh.label": "Énergie journalière (kWh/jour)",
    "wiz.lf.label": "Facteur de charge (0,1–0,95)",
    "wiz.lf.derived_label": "Facteur de charge",
    "wiz.altitude.label": "Altitude (m)",
    "wiz.ambient.label": "Température ambiante de conception (°C)",
    "wiz.irradiance.label": "Irradiation solaire (kWh/m²/jour)",
    "wiz.site.derate_banner": "La puissance des groupes est déclassée d'environ 4\u00a0% par tranche de 300\u00a0m au-delà de 1\u00a0000\u00a0m d'altitude et d'environ 2\u00a0% par tranche de 5\u00a0°C au-delà de 40\u00a0°C. Nous compensons par un surdimensionnement.",
    "wiz.grid.on_grid": "Sur réseau (fiable)",
    "wiz.grid.weak_grid": "Réseau faible (coupures)",
    "wiz.grid.off_grid": "Hors réseau",
    "wiz.outage.label": "Heures de coupure par jour (moyenne)",
    "wiz.fuel_price.label": "Prix du carburant (USD/L) — valeur par défaut du pays ({{country}})",
    "wiz.own.captive": "Captif (autoconsommation)",
    "wiz.own.IPP": "IPP (vente au réseau)",
    "wiz.own.BOO": "BOO (construire-posséder-exploiter)",
    "wiz.own.PPP": "PPP",
    "wiz.own.ESCO": "ESCO (services énergétiques)",
    "wiz.own.lease": "Location",
    "wiz.th.gas_engine.title": "Moteur à gaz alternatif",
    "wiz.th.gas_engine.body": "Modulaire 1–10\u00a0MW/unité, démarrage rapide, bon rendement à charge partielle, compatible cogénération.",
    "wiz.th.ocgt.title": "Turbine à gaz à cycle ouvert (OCGT)",
    "wiz.th.ocgt.body": "5–60\u00a0MW, pointe / montée en charge rapide, fort déclassement selon la température ambiante.",
    "wiz.th.ccgt.title": "Cycle combiné (CCGT / CCP)",
    "wiz.th.ccgt.body": "30–500+\u00a0MW, rendement le plus élevé, fonctionnement en base, nécessite de l'eau.",
    "wiz.th.disabled": "Désactivé par l'administrateur.",
    "wiz.th.below_min": "La pointe du site ({{peak}}\u00a0MW) est inférieure au minimum de {{min}}\u00a0MW pour cette technologie.",
    "wiz.th.count": "Nombre",
    "wiz.th.mw_unit": "MW/unité",
    "wiz.th.chp.title": "CHP / cogénération",
    "wiz.th.chp.sub": "Récupère ~40–50\u00a0% de l'énergie du combustible sous forme de chaleur utile (nécessite un besoin thermique sur site).",
    "wiz.th.chp.host": "Machine hôte",
    "wiz.th.heat_demand": "Besoin thermique du site (kW)",
    "wiz.th.gas_fuel": "Combustible gaz",
    "wiz.th.source": "Source",
    "wiz.th.src.pipeline": "Gaz par pipeline",
    "wiz.th.src.lng": "GNL (importé / regazéifié)",
    "wiz.th.src.cng": "GNC – pipeline virtuel (livré par camion)",
    "wiz.th.src.lpg": "GPL",
    "wiz.th.gas_available": "Gaz disponible sur site",
    "wiz.th.gas_price": "Prix livré ($/GJ)",
    "wiz.th.ambient.title": "Conditions ambiantes (déclassement thermique)",
    "wiz.th.temp": "Température (°C)",
    "wiz.th.altitude": "Altitude (m)",
    "wiz.th.derate": "Appliquer le déclassement",
    "wiz.th.footnote": "Tous les coefficients sont indicatifs, de niveau préliminaire. Le dimensionnement détaillé, les courbes constructeur et les contrats gaz relèvent de la phase de faisabilité payante.",
    "wiz.toast.incomplete": "Veuillez compléter cette étape",
    "wiz.toast.compute_failed": "Calcul impossible. Veuillez réessayer.",

    "results.carbon.rating.high": "ÉLEVÉ",
    "results.carbon.drop_into_low": "Passer en Bas",
    "results.carbon.tweak.chp_copy": "Activez la cogénération (CHP) sur vos {{tech}} — le crédit d'évitement chaudière fait typiquement baisser les émissions nettes de ~15–25 %, à environ {{projected}} gCO₂/kWh, lorsque le site a une réelle demande thermique.",
    "results.carbon.reduce_emissions": "Réduire les émissions",
    "results.carbon.tech.gas_engine": "moteurs à gaz",
    "results.carbon.tech.ocgt": "turbines à gaz (OCGT)",
    "results.carbon.tech.ccgt": "centrale à cycle combiné (CCGT)",
    "results.carbon.tweak.chp_badge": "Essayer la CHP",
    "results.carbon.tweak.ppa_copy": "Signez un PPA électricité renouvelable (ou des garanties d'origine) pour la part importée du réseau — un facteur d'émissions réseau nul fait chuter le score immédiatement sans changer l'équipement sur site.",
    "results.carbon.tweak.ppa_badge": "Essayer un PPA renouvelable",
    "results.carbon.tweak.solar_battery_copy": "Essayez d'ajouter ~{{battery}} kWh de capacité batterie et ~{{solar}} kWc de solaire. Cela déplacerait environ {{diesel}} MWh/an de dispatch diesel et ferait basculer le score en Bas.",
    "results.carbon.tweak.solar_battery_badge": "Essayer +{{battery}} kWh batterie",
    "results.carbon.tweak.try_it": "Essayer — pré-remplir le panneau Ajuster",
    "results.carbon.applied_toast": "Panneau Ajuster pré-rempli — recalcul en cours…",
    "results.paywall.overline": "Rapport détaillé",
    "results.paywall.title": "Débloquez l'analyse complète",
    "results.paywall.no_viable": "Aucune centrale ne répond aux besoins de votre site avec ces données : il n'y a donc pas de rapport à acheter. Modifiez les données, par exemple la disponibilité du carburant, puis relancez le calcul.",
    "results.paywall.desc": "Détail complet CAPEX / OPEX, LCOE, autorisations par pays, mémorandum de faisabilité (PDF) et plan initial projet & financier.",
    "results.paywall.buy_report": "Acheter le rapport",
    "results.paywall.bundle": "Pack",
    "results.paywall.use_credit": "Utiliser 1 crédit",
    "results.paywall.credits_left": "restants",
    "results.paywall.promo_label": "Vous avez un code promo ?",
    "results.paywall.promo_placeholder": "Saisir le code",
    "results.paywall.promo_redeem": "Valider",
    "results.paywall.promo_applying": "Application…",
    "results.locked.equipment": "Configuration matérielle",
    "results.locked.equipment_sub": "Liste des composants",
    "results.locked.costs": "Coûts budgétaires",
    "results.locked.costs_sub": "Détail CAPEX, OPEX, LCOE",
    "results.locked.permitting": "Autorisations & réglementation",
    "results.kpi.lcoe": "LCOE",
    "results.kpi.opex": "OPEX annuel",
    "results.kpi.payback": "Retour sur investissement",
    "results.kpi.payback_hint": "Années nécessaires pour récupérer le CAPEX total de la centrale grâce à ses économies annuelles par rapport au cas de comparaison.",
    "results.kpi.life_used": "Période d'analyse",
    "results.report_unlocked": "Rapport débloqué",
    "results.open_report": "Ouvrir le rapport détaillé",
    "results.disclaimer": "Le Power Configurator fournit des résultats indicatifs, de niveau préliminaire, à titre informatif uniquement. Il ne constitue pas un conseil d'ingénierie, financier ou d'investissement.",
    "results.signin_save": "Connectez-vous pour enregistrer ce projet",
    "results.saved_adjusted": "Projet enregistré avec votre configuration ajustée",
    "results.saved_ok": "Projet enregistré",
    "results.save_failed": "Impossible d'enregistrer",
    "results.save_failed_project": "Impossible d'enregistrer le projet",
    "results.signin_buy": "Connectez-vous pour acheter le rapport détaillé",
    "results.credit_unlocked": "Rapport débloqué avec 1 crédit",
    "results.no_credits": "Pas de crédits — veuillez acheter un pack",
    "results.unlock_failed": "Échec du déblocage",
    "results.enter_promo": "Saisissez un code promo",
    "results.promo_applied": "Code promo appliqué — rapport débloqué",
    "results.promo_failed": "Impossible de valider le code",
    "results.credit_added": "{{n}} crédits ajoutés à votre compte",
    "results.credit_added_1": "1 crédit ajouté à votre compte",
    // Sensitivity
    "sens.title": "Ajustez le prix du combustible et l'irradiance",
    "sens.title.fuel_only": "Ajustez le prix du combustible",
    "sens.chart.no_solar": "Cette centrale n'a pas de solaire : l'irradiance ne change pas son coût.",
    "frontier.unavailable": "Alternative indisponible pour cette saisie",
    "sens.overline": "Sensibilité",
    "sens.desc": "Déplacez les curseurs ; LCOE et retour sur investissement se mettent à jour en temps réel.",
    "sens.fuel": "Prix du gasoil (USD/L)",
    "sens.axis.gas_price": "Prix du gaz livré (USD/GJ)",
    "sens.irradiance": "Irradiance solaire (kWh/m²/jour)",
    "sens.reset": "réinitialiser",
    "sens.baseline": "référence",
    "sens.chart.title": "Courbes de sensibilité du LCOE",
    "sens.chart.desc": "Balayage de −30\u00a0% à +30\u00a0% de la référence. Le repère indique la valeur actuelle du curseur.",
    "sens.tile.lcoe": "LCOE",
    "sens.tile.capex": "CAPEX",
    "sens.tile.opex": "OPEX annuel",
    "sens.tile.payback": "Retour sur invest.",
    "sens.tile.payback_yr": "ans",
    "sens.chart.xaxis": "% par rapport à la référence",
    "sens.chart.tooltip_axis": "par rapport à la référence",
    "sens.chart.legend.fuel": "LCOE vs prix du gasoil",
    "sens.chart.legend.gas": "LCOE vs prix du gaz",
    "sens.chart.legend.irr": "LCOE vs irradiance",
    "sens.chart.loading": "chargement…",
    "sens.chart.baseline_prefix": "LCOE de référence :",
    "sens.chart.baseline_suffix_fuel": "au prix carburant",
    "sens.chart.baseline_suffix_irr": "irr.",
    // Compare
    "cmp.title": "Comparer les scénarios",
    "cmp.overline": "Comparaison",
    "cmp.desc": "Sélectionnez 2 à 4 projets enregistrés pour les comparer côte à côte. Le meilleur résultat par indicateur apparaît en vert ; le moins bon en orange.",
    "cmp.back": "Retour aux projets",
    "cmp.saved": "Vos projets enregistrés",
    "cmp.pick_at_least_2": "Sélectionnez au moins 2 projets à comparer",
    "cmp.pick_at_least_2_sub": "Meilleur par indicateur en vert ; moins bon en orange.",
    "cmp.export_csv": "Exporter en CSV",
    "report.export_csv": "Télécharger le CSV",
    "report.csv_downloaded": "CSV téléchargé",
    "report.roadmap.title": "Programme indicatif des étapes",
    "report.roadmap.phase.development": "Développement & faisabilité",
    "report.roadmap.phase.permitting": "Autorisations",
    "report.roadmap.phase.engineering": "Ingénierie",
    "report.roadmap.phase.mobilization": "Mobilisation",
    "report.roadmap.phase.construction": "Construction & mise en service",
    "report.roadmap.phase.equipment_lead": "Délai de livraison des équipements",
    "report.roadmap.phase.operation": "Exploitation",
    "report.roadmap.financial_close": "Clôture financière",
    "report.roadmap.cod": "Mise en service",
    "report.roadmap.month": "mois",
    "report.roadmap.months_label": "mois",
    "report.roadmap.months": "mois",
    "report.roadmap.from_month": "à partir du mois",
    "report.roadmap.ongoing": "en continu",
    "report.roadmap.tech.gas_engine": "moteurs à gaz",
    "report.roadmap.tech.ocgt": "OCGT",
    "report.roadmap.tech.ccgt": "CCGT",
    "report.roadmap.lead_note": "La mise en service est fixée par le délai de livraison {tech} ({lead} mois + {build} mois de construction), et non par le calendrier de base.",
    "results.cod.title": "Mise en service commerciale",
    "results.cod.month": "mois",
    "results.cod.sub": "à compter du lancement du projet",
    // Share
    "share.title": "Partager ce rapport",
    "share.overline": "Partage équipe · lecture seule",
    "share.desc_prefix": "Toute personne disposant du lien peut consulter",
    "share.desc_suffix": "en lecture seule. Les données brutes ne peuvent être téléchargées qu'au format PDF. Vous pouvez révoquer le lien à tout moment.",
    "share.expiry": "Expire dans",
    "share.expiry.7": "7 jours",
    "share.expiry.30": "30 jours",
    "share.expiry.90": "90 jours",
    "share.expiry.never": "Jamais",
    "share.copy": "Copier",
    "share.copied": "Copié",
    "share.revoke": "Révoquer",
    "share.revoking": "Révocation…",
    "share.preview": "Ouvrir l'aperçu →",
    "share.generating": "Génération du lien…",
    // Common
    "common.language": "Langue",
    "common.download_pdf": "Télécharger le PDF",
    "common.reset": "Réinitialiser",
  },
};

const detectBrowserLocale = () => {
  try {
    // navigator.languages is the ordered preference list; index 0 is the top choice.
    const langs = (typeof navigator !== "undefined" && navigator.languages) || [];
    const top = (langs[0] || (typeof navigator !== "undefined" && navigator.language) || "").toLowerCase();
    if (top.startsWith("fr")) return "fr";
    return "en";
  } catch {
    return "en";
  }
};

const initialLocale = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "fr") return stored;
  } catch (err) {
    console.debug("[i18n] localStorage read blocked", err?.message);
  }
  return detectBrowserLocale();
};

let currentLocale = typeof window !== "undefined" ? initialLocale() : "en";
const listeners = new Set();

export const getLocale = () => currentLocale;

export const setLocale = (loc, opts = { persist: true }) => {
  if (loc !== "en" && loc !== "fr") return;
  currentLocale = loc;
  if (opts.persist) {
    try {
      localStorage.setItem(STORAGE_KEY, loc);
    } catch (err) {
      console.debug("[i18n] localStorage write blocked", err?.message);
    }
  }
  listeners.forEach((fn) => fn(loc));
};

export const suggestLocaleFromCountry = (country) => {
  if (!country) return;
  // Only auto-suggest FR the first time. Respect explicit user choice.
  let userChose = false;
  try {
    userChose = !!localStorage.getItem(STORAGE_KEY);
  } catch (err) {
    console.debug("[i18n] localStorage read blocked", err?.message);
  }
  if (userChose) return;
  if (FRANCOPHONE_COUNTRIES.has(country)) {
    setLocale("fr", { persist: false });
  }
};

/** True when the current locale came from browser auto-detect (never persisted a user choice). */
export const wasAutoDetected = () => {
  try {
    return !localStorage.getItem(STORAGE_KEY);
  } catch {
    return true;
  }
};

export const t = (key, fallback) => {
  const d = DICT[currentLocale] || DICT.en;
  return d[key] ?? DICT.en[key] ?? fallback ?? key;
};

export const tr = (en, fr) => (currentLocale === "fr" ? fr : en);

const syncHtmlLang = (loc) => { if (typeof document !== "undefined") document.documentElement.lang = loc; };
syncHtmlLang(currentLocale);
listeners.add(syncHtmlLang);

export const useT = () => {
  const [, rerender] = useState(0);
  useEffect(() => {
    const fn = () => rerender((n) => n + 1);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  return useCallback((key, fallback) => t(key, fallback), []);
};

export { DICT };
